/**
 * HTTP client for the ora workflow runtime — the v2 add-in's contract
 * layer to the Product API proxy at `/api/workflows/*` and `/api/clients/*`.
 *
 * Two modes, controlled by `VITE_USE_STUB_API`:
 *
 *   1. Real mode (default once substrate ships) — POSTs / GETs to the
 *      Product API which forwards to the workflow substrate via
 *      `IMcpWorkflowsClient`. See PluginWorkflowController.cs for the
 *      wire shape (snake_case via [JsonPropertyName]).
 *
 *   2. Stub mode (`VITE_USE_STUB_API=1`) — returns plausible objects
 *      shaped exactly like the substrate's test fixtures so the UI can
 *      be exercised without a backend. Stub bypasses authClient.
 *
 * Phase-1 invariants enforced by this module:
 *
 *   * Per-request `x-correlation-id` header (newCorrelationId()).
 *   * Generalised `triggerWorkflow(workflowId, inputs)` with caller-
 *     supplied `inputs.idempotency_key` so retries do NOT spawn duplicate
 *     instances on the substrate side.
 *   * `ApiError` class carrying everything support needs to triage:
 *     `{ message, status, url, method, correlation_id, request_body?,
 *        response_body?, auth_tier?, timestamp }`.
 *   * Retry-once on 5xx + network failure with the SAME idempotency_key.
 *   * 30s AbortController timeout per request.
 *   * Every realApi method has a `stubApi` counterpart with matching
 *     signature.
 */

import type { OversightLevel } from "@product-app/common/workflows/oversightLevels";
import {
  isAcknowledgement,
  type AcknowledgeWarningResult,
} from "@product-app/common/workflows/warningAcknowledgement";
import { getProductApiToken } from "./authClient";
import { API_BASE_URL } from "../config/apiBaseUrl";
import { collectSilentTriggerFields } from "./silentTriggerFields";
import { getSharedMailboxLaunchTarget } from "../features/sharedMailbox/sharedMailboxTarget";
import { resolveTriageMailbox } from "../features/sharedMailbox/triageMailbox";
import { logger, newCorrelationId, type AuthTier } from "../obs/logger";
import {
  DEMO_ACTIVE_WORKFLOWS,
  DEMO_CATALOG,
  DEMO_CLIENT_DETAILS,
  DEMO_HITL_QUESTION,
  DEMO_INSTANCE_LOGS,
  DEMO_INSTANCE_STATUSES,
  DEMO_POLICIES_BY_CLIENT_SK,
  DEMO_PUSH_CHANNEL,
  DEMO_USER_PUSH_CHANNEL,
  DEMO_WAITING_QUEUE,
  demoEmailRunStateFor,
} from "../data/demoData";
import type {
  ActiveWorkflow,
  EmailRunStateResponse,
  InstanceLogEntry,
  InstanceStatus,
  PushChannelDescriptor,
  WaitingForMeResponse,
  WaitingOnMeResponse,
  ReadyBriefsResponse,
  ReadyBriefItem,
  WorkflowCatalogEntry,
  WorkflowKpis,
} from "../types/workflow";
import type { ClientContext, ClientDetails, ClientSearchResult, PolicyInForce } from "../types/client";
import type { WorkflowResultBody } from "@product-app/common/workflows/result.types";
import { excludeCsvArtifacts } from "@product-app/common/workflows/artifactFilters";
import { isCampaignRow } from "@product-app/common/workflows/orafixCampaignLink";
import type { SentReplySweepOutcome } from "@product-app/common/workflows/components/SentReplySweepCard";
// `hashPrefillInputs` / `buildPrefillInputs` — moved to `core/prefillInputs`
// (plan glittery-stargazing-treehouse Phase 3 step 2) so both the add-in and
// ora hash identically and reuse the same completed prefill instance.
// `hashPrefillInputs` is re-exported below (byte-identical implementation)
// in case anything still imports it from this module.
import {
  buildPrefillInputs,
  hashPrefillInputs,
} from "@product-app/common/workflows/launch/core/prefillInputs";
export { hashPrefillInputs };

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

function resolveStubFlag(): boolean {
  // Vite injects VITE_USE_STUB_API into import.meta.env. We intentionally
  // avoid `import.meta.env` here so ts-jest can parse this file.
  // Fallback chain: __OUTLOOK_ADDIN_USE_STUB__ build-global → process.env → false.
  // Tests can also force stub mode by setting `globalThis.__OUTLOOK_ADDIN_USE_STUB__`.
  const g = globalThis as unknown as { __OUTLOOK_ADDIN_USE_STUB__?: string | boolean };
  if (typeof g.__OUTLOOK_ADDIN_USE_STUB__ !== "undefined") {
    const v = g.__OUTLOOK_ADDIN_USE_STUB__;
    return v === true || v === "1" || v === "true";
  }
  const env =
    (globalThis as unknown as { process?: { env?: Record<string, string | undefined> } })
      .process?.env ?? {};
  return env.VITE_USE_STUB_API === "1";
}

/**
 * Stub-mode value for the `shared-mailbox-disabled` KILL SWITCH. NEGATIVE flag:
 * the returned `isEnabled` means "feature killed", so the default `false` keeps
 * shared mailbox ON in stub/demo mode (default-on). Mirrors `resolveStubFlag()`
 * above — deliberately avoids `import.meta.env` here (ts-jest can't parse it in
 * this file; see `resolveStubFlag`'s comment). Tests can force the kill via
 * `globalThis.__OUTLOOK_ADDIN_STUB_SHARED_MAILBOX_FLAG__`.
 */
function resolveStubSharedMailboxFlag(): boolean {
  const g = globalThis as unknown as {
    __OUTLOOK_ADDIN_STUB_SHARED_MAILBOX_FLAG__?: string | boolean;
  };
  if (typeof g.__OUTLOOK_ADDIN_STUB_SHARED_MAILBOX_FLAG__ !== "undefined") {
    const v = g.__OUTLOOK_ADDIN_STUB_SHARED_MAILBOX_FLAG__;
    return v === true || v === "1" || v === "true";
  }
  const env =
    (globalThis as unknown as { process?: { env?: Record<string, string | undefined> } })
      .process?.env ?? {};
  return env.VITE_STUB_SHARED_MAILBOX_FLAG === "1";
}

// Bumped 15s -> 30s (2026-07-09): the add-in's mount calls (getCatalog /
// listInstances / KPIs) were going red at ~15s when the substrate was saturated
// by concurrent analyze_email calls that each fan out 20-40 sequential Databricks
// SQL round-trips (56-127s). Empty catalog -> LaunchTab renders no workflows.
// Interim headroom until analyze_email's client-resolution calls are batched;
// analyzeEmailSync already overrides to 120s so this only affects the fast calls.
const REQUEST_TIMEOUT_MS = 30_000;

/** Shared poll timeout matching the substrate's ANALYZE_EMAIL_DEADLINE_SECONDS (300s). */
export const PREFILL_POLL_TIMEOUT_MS = 290_000;

// ---------------------------------------------------------------------------
// ApiError
// ---------------------------------------------------------------------------

/**
 * Keys whose values must NEVER appear in error envelopes or in the
 * "Technical details" clipboard payload that the support UI surfaces.
 * Adding a key here is preferable to redacting on the consumer side.
 */
const SENSITIVE_BODY_KEYS = new Set([
  "am_graph_access_token",
  "am_graph_refresh_token",
  "original_eml_b64",
  "value", // flow credential password — never appears in error envelopes
]);

/**
 * Deep-walk a JSON-serialisable value and replace any keyed entry whose key
 * matches ``SENSITIVE_BODY_KEYS`` with the string "<redacted>". Arrays and
 * nested objects are walked recursively. Non-objects pass through unchanged.
 */
function scrubSensitive(value: unknown): unknown {
  if (value === null || typeof value !== "object") return value;
  if (Array.isArray(value)) return value.map(scrubSensitive);
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = SENSITIVE_BODY_KEYS.has(k) ? "<redacted>" : scrubSensitive(v);
  }
  return out;
}

export interface ApiErrorPayload {
  message: string;
  status: number | null;
  url: string;
  method: string;
  correlation_id: string;
  request_body?: unknown;
  response_body?: unknown;
  auth_tier?: AuthTier | null;
  timestamp: string;
}

export class ApiError extends Error implements ApiErrorPayload {
  readonly status: number | null;
  readonly url: string;
  readonly method: string;
  readonly correlation_id: string;
  readonly request_body?: unknown;
  readonly response_body?: unknown;
  readonly auth_tier?: AuthTier | null;
  readonly timestamp: string;

  constructor(payload: ApiErrorPayload) {
    super(payload.message);
    this.name = "ApiError";
    this.status = payload.status;
    this.url = payload.url;
    this.method = payload.method;
    this.correlation_id = payload.correlation_id;
    this.request_body = payload.request_body;
    this.response_body = payload.response_body;
    this.auth_tier = payload.auth_tier ?? null;
    this.timestamp = payload.timestamp;
  }

  /** Convenience for ErrorPanel copy-to-clipboard. */
  toJSON(): ApiErrorPayload {
    return {
      message: this.message,
      status: this.status,
      url: this.url,
      method: this.method,
      correlation_id: this.correlation_id,
      request_body: this.request_body,
      response_body: this.response_body,
      auth_tier: this.auth_tier ?? null,
      timestamp: this.timestamp,
    };
  }
}

// ---------------------------------------------------------------------------
// Internal fetch helpers — single point of correlation_id, retry, timeout.
// ---------------------------------------------------------------------------

/** A card click's detail read: the AM is watching, so fail fast to the fallback. */
const READY_BRIEF_DETAIL_TIMEOUT_MS = 8_000;

interface RequestOptions {
  method: "GET" | "POST" | "PATCH" | "DELETE";
  path: string;
  body?: unknown;
  /** Skip retry-once even for 5xx. Used by side-effecting POSTs that are
   *  NOT idempotent (i.e., callers that did not supply an idempotency_key). */
  noRetry?: boolean;
  /** Override the per-request timeout (test scaffolding only). */
  timeoutMs?: number;
}

interface RawResponseInfo {
  status: number;
  ok: boolean;
  text: string;
  json: unknown | null;
}

/**
 * Inject the bearer token. Wired through useAuth's cascade once Phase-2
 * lands; today defers to authClient (Tier 2). Surfaced as a setter so
 * tests can short-circuit without mocking the entire authClient.
 */
let _tokenProvider: () => Promise<string> = getProductApiToken;
let _authTierProvider: () => AuthTier | null = () => "dialog_handoff";

export function _setTokenProviderForTests(
  provider: () => Promise<string>,
  tier: AuthTier | null = null,
): void {
  _tokenProvider = provider;
  _authTierProvider = () => tier;
}

export function _resetTokenProviderForTests(): void {
  _tokenProvider = getProductApiToken;
  _authTierProvider = () => "dialog_handoff";
}

/**
 * Bridge from useAuth.ts. `import` cycle prevention: orchestratorApi
 * doesn't pull useAuth directly; consumers call `wireAuth(useAuthHook)`
 * once at app boot.
 */
export function wireAuth(
  getToken: () => Promise<string>,
  getAuthTier: () => AuthTier | null,
): void {
  _tokenProvider = getToken;
  _authTierProvider = getAuthTier;
}

async function rawFetch(
  url: string,
  init: RequestInit,
  timeoutMs: number,
): Promise<RawResponseInfo> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, { ...init, signal: controller.signal });
    const text = await response.text();
    let json: unknown | null = null;
    if (text) {
      try {
        json = JSON.parse(text);
      } catch {
        json = null;
      }
    }
    return { status: response.status, ok: response.ok, text, json };
  } finally {
    clearTimeout(timeout);
  }
}

function shouldRetry(status: number | null, err: unknown): boolean {
  if (status !== null && status >= 500 && status < 600) return true;
  // Network errors / aborts produce TypeError under fetch; retry once.
  if (err && err instanceof TypeError) return true;
  return false;
}

async function request<T>(opts: RequestOptions): Promise<T> {
  const correlationId = newCorrelationId();
  const url = `${API_BASE_URL}${opts.path}`;
  const method = opts.method;
  const timeoutMs = opts.timeoutMs ?? REQUEST_TIMEOUT_MS;
  const auth_tier = _authTierProvider();

  const token = await _tokenProvider();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    "x-correlation-id": correlationId,
    // Stories 26742/26747: tells Product.Api which UI surface originated the
    // call, so it can forward X-Caller-Surface: outlook_addin to the
    // substrate. Sent on EVERY request from this single shared function —
    // ora's workflows.service.server.tsx sends "ora_web" the same way on its
    // own calls.
    "X-Ora-Surface": "outlook_addin",
  };
  const init: RequestInit = {
    method,
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  };

  const attempt = async (attemptNumber: number): Promise<RawResponseInfo> => {
    logger.debug("orchestratorApi: request attempt", {
      correlation_id: correlationId,
      auth_tier: auth_tier ?? undefined,
      url,
      method,
      attempt: attemptNumber,
    });
    return rawFetch(url, init, timeoutMs);
  };

  let info: RawResponseInfo | null = null;
  let lastErr: unknown = null;

  // Attempt 1
  try {
    info = await attempt(1);
  } catch (err) {
    lastErr = err;
  }

  // Retry once on 5xx OR network failure, unless explicitly disabled.
  // Idempotency_key in the body protects against duplicate side effects.
  const retryEligible = !opts.noRetry && shouldRetry(info?.status ?? null, lastErr);
  if (retryEligible) {
    logger.warn("orchestratorApi: retrying once", {
      correlation_id: correlationId,
      url,
      method,
      first_status: info?.status ?? null,
      first_error: lastErr ? String(lastErr) : undefined,
    });
    try {
      info = await attempt(2);
      lastErr = null;
    } catch (err) {
      lastErr = err;
      info = null;
    }
  }

  if (!info) {
    // Network / timeout / abort — include the endpoint so the user can see which API was unreachable.
    throw new ApiError({
      message: `Network error reaching ${method} ${url}: ${String(lastErr)}`,
      status: null,
      url,
      method,
      correlation_id: correlationId,
      request_body: scrubSensitive(opts.body),
      response_body: null,
      auth_tier,
      timestamp: new Date().toISOString(),
    });
  }

  if (!info.ok) {
    // Prefer the API-supplied detail/title from ProblemDetails over a generic
    // HTTP status line so specific messages (e.g. "Workflow service unavailable")
    // surface in the UI.
    const body = info.json as Record<string, unknown> | null;
    const apiMessage =
      (typeof body?.detail === "string" && body.detail) ||
      (typeof body?.title === "string" && body.title) ||
      `API ${info.status} ${method} ${opts.path}`;
    throw new ApiError({
      message: apiMessage,
      status: info.status,
      url,
      method,
      correlation_id: correlationId,
      request_body: scrubSensitive(opts.body),
      response_body: info.json ?? info.text,
      auth_tier,
      timestamp: new Date().toISOString(),
    });
  }

  return (info.json as T) ?? (undefined as unknown as T);
}


/**
 * GET a stored file through the relay with the add-in's bearer (story 25240).
 * Shared components install this via setStoredFileFetcher (main.tsx) and read
 * the body as a Blob. Not routed through request(): that helper reads every
 * body as text and JSON-parses it.
 *
 * Same abort timeout as request() (REQUEST_TIMEOUT_MS), here until the response
 * starts: a host that never answers rejects the fetch, so the UI shows
 * "unavailable". The body read is not timed: a multi-MB trace download may take
 * longer, and the relay's idle timeout bounds a stalled API.
 */
export async function fetchStoredFileResponse(path: string): Promise<Response> {
  const token = await _tokenProvider();
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    return await fetch(`${API_BASE_URL}${path}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "x-correlation-id": newCorrelationId(),
        "X-Ora-Surface": "outlook_addin",
      },
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timeout);
  }
}

// ---------------------------------------------------------------------------
// Public API surface
// ---------------------------------------------------------------------------

/** Body shape for triggerWorkflow. */
export interface TriggerWorkflowInputs {
  /** Substrate idempotency token; SAME value across retries. */
  idempotency_key?: string;
  /** Free-form workflow inputs — workflow-specific. */
  [key: string]: unknown;
}

export interface TriggerWorkflowResult {
  instance_id: string;
  workflow_id: string;
  major_version: number;
  status: string;
  warning?: boolean;
}

/** Body for cancel — substrate has no required fields. */
export interface CancelInstanceResult {
  instance_id: string;
  status: string;
}

/** Body for resume / HITL approve. */
export interface ResumeInstanceResult {
  instance_id: string;
  status: string;
}

/** Email payload used by analyze_email. */
export interface AnalyzeEmailPayload {
  email_subject: string;
  email_body: string;
  email_sender: string;
  /** RFC 5322 Message-ID — analyze_email retrieve-first cache key. */
  internet_message_id?: string;
  email_id?: string;
  email_received_date?: string;
  /**
   * Graph/REST message id. The substrate reads the email's PDF attachments from
   * {@link mailbox_address} via Graph using this id — the add-in no longer
   * fetches attachment bytes client-side.
   */
  email_rest_id?: string;
  /**
   * Mailbox owner SMTP. The substrate reads the message from this mailbox via
   * app-only Graph (paired with {@link email_rest_id}).
   */
  mailbox_address?: string;
  /**
   * When true, extract client/policy but do not persist a MODEL workflow
   * recommendation. Pair with {@link selected_workflow_id} to stamp the AM's
   * pick as the cached recommendation so the brief shows in Ready to start.
   */
  skip_workflow_recommendation?: boolean;
  /** AM-picked workflow id — persisted as the recommendation, not a model guess. */
  selected_workflow_id?: string;
}

/**
 * Minimal email-context shape the payload builder needs. `EmailContext`
 * (useMailboxItem) satisfies this structurally — kept local so this service
 * doesn't import from the hooks layer.
 */
export interface AnalyzeEmailSource {
  subject: string;
  body: string;
  sender: string;
  emailId?: string;
  receivedDate?: string;
  restId?: string;
  mailboxAddress?: string;
  /** RFC 5322 Message-ID — analyze_email retrieve-first cache key. */
  internetMessageId?: string;
}

/**
 * Single source of truth for the analyze_email request payload. Both analyze
 * paths (LaunchTab "Suggest" and useWorkflowStatus "process") MUST build the
 * payload through here so fields like email_rest_id / mailbox_address (needed
 * for the substrate's Graph PDF read) can never be wired into one path and
 * forgotten in the other.
 */
export function buildAnalyzeEmailPayload(src: AnalyzeEmailSource): AnalyzeEmailPayload {
  return {
    email_subject: src.subject,
    email_body: src.body,
    email_sender: src.sender,
    email_id: src.emailId,
    email_received_date: src.receivedDate,
    email_rest_id: src.restId,
    // Story 26727: the mailbox the message was actually read FROM — the
    // detected SHARED target when one exists, else the signed-in reader's own
    // address — never `userProfile.emailAddress` alone. `email_analysis` is
    // now keyed on `(internet_message_id, mailbox_address)`; sending the
    // wrong half of that key here is what used to key this call's row
    // differently from `reanalyzeEmail`'s for the SAME email.
    mailbox_address: resolveTriageMailbox(src) ?? src.mailboxAddress,
    // Retrieve-first cache key — must ride every analyze path (LaunchTab
    // "Suggest" and useWorkflowStatus "process"), else a re-open misses cache.
    // Fall back when the host doesn't surface a RFC 5322 Message-ID (some
    // Outlook hosts return an empty internetMessageId). restId FIRST: the
    // substrate's own server-side fallback keys the cache row by email_rest_id,
    // so using the same value here keeps client- and server-derived keys
    // identical. The EWS item id is the last resort.
    internet_message_id: src.internetMessageId || src.restId || src.emailId,
  };
}

/** Legacy endorsement DTOs preserved for the v1 PlanReview surface. */
export interface ExecutePayload extends AnalyzeEmailPayload {
  attachments: Array<{ name: string; contentType: string }>;
  workflowType?: string;
  branch_filter?: string;
  // v1 alias forms — caller may set either.
  emailSubject?: string;
  emailBody?: string;
  emailSender?: string;
  emailId?: string;
  emailReceivedDate?: string;
}

export interface ApprovePayload {
  approved: boolean;
  corrections?: Record<string, unknown>;
  rejectionReason?: string;
  rejection_reason?: string;
}

export interface OrchestratorResponse {
  workflow_id?: string;
  instance_id?: string;
  status: string;
  plan?: Record<string, unknown>;
  error?: string;
  carrier_handoff?: Record<string, unknown>;
  execution_time_seconds?: number;
}

export interface ArtifactDto {
  kind: string;
  url: string;
  expires_at?: string;
  step_name?: string;
  size_bytes?: number;
}

export interface ArtifactsResponse {
  instance_id: string;
  artifacts: ArtifactDto[];
}

/**
 * One persisted HITL Q&A audit record. Mirrors HitlEventDto on the Product
 * API side. Question and Response are arbitrary JSON shapes — the substrate
 * is the schema source of truth and shapes evolve per workflow.
 */
export interface HitlEventDto {
  sequence: number;
  step_id: string;
  phase_id: string | null;
  event_type: string;
  question: Record<string, unknown> | null;
  prompt_text: string | null;
  response: Record<string, unknown> | null;
  artifacts_snapshot: string[];
  asked_at: string;
  answered_at: string | null;
  answered_by: string | null;
  answered_by_name: string | null;
}

export interface HitlHistoryResponse {
  instance_id: string;
  events: HitlEventDto[];
}

// ---------------------------------------------------------------------------
// run record (story 26742) + touchpoint telemetry (story 26747).
// ---------------------------------------------------------------------------

/** Parsed + name-resolved form of a decision_log `actor` string. Mirrors
 *  RunDecisionActorDto on the Product API side. */
export interface RunDecisionActor {
  kind: string;
  identifier: string;
  name: string | null;
}

/** One decision_log row. Mirrors RunDecisionDto. `before`/`after` are
 *  arbitrary JSON — the substrate is the schema source of truth. */
export interface RunDecisionDto {
  ts: string;
  event_type: string;
  actor: RunDecisionActor;
  surface: string | null;
  authority: string | null;
  subject_type: string;
  subject_id: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  reason: string | null;
  trace_id: string | null;
}

/** One HITL audit row, as get_run_decisions projects it — same underlying
 *  data as HitlEventDto, plus `surface`. Mirrors RunHitlAnswerDto. */
export interface RunHitlAnswerDto {
  sequence: number;
  step_id: string;
  phase_id: string | null;
  event_type: string;
  asked_at: string;
  answered_at: string | null;
  answered_by: string | null;
  answered_by_name: string | null;
  surface: string | null;
  prompt_text: string | null;
  response: Record<string, unknown> | null;
  artifacts_snapshot: string[];
}

/** One hitl_notifications row. Mirrors RunNotificationDto.
 *  `source_message_lost` is null until migration 0064 ships the column. */
export interface RunNotificationDto {
  decision_key: string;
  decision_id: string | null;
  recipient: string | null;
  status: string;
  attempts: number;
  failure_reason: string | null;
  failure_kind: string | null;
  link: string | null;
  subject: string | null;
  created_at: string | null;
  updated_at: string | null;
  source_message_lost: boolean | null;
}

export interface RunTouchpointsDto {
  planned: number;
  exception: number;
  attention_seconds: number;
}

export interface RunDecisionCountsDto {
  decisions: number;
  hitl_answers: number;
  notifications: number;
  notifications_failed: number;
}

/**
 * `GET /api/workflows/instances/:instanceId/decisions` response. `degraded`
 * is true when the substrate's get_run_decisions tool could not be reached
 * (not yet deployed, or any other failure) — every list is then empty, and
 * that is NEVER the same thing as "the run genuinely has no decisions yet".
 */
export interface RunDecisionsResponse {
  run_id: string;
  workflow_id: string | null;
  status: string | null;
  summary: string | null;
  decisions: RunDecisionDto[];
  hitl_answers: RunHitlAnswerDto[];
  notifications: RunNotificationDto[];
  touchpoints: RunTouchpointsDto | null;
  counts: RunDecisionCountsDto | null;
  degraded?: boolean;
}

/**
 * One arrive/leave touchpoint event (story 26747). Opaque on purpose beyond
 * the envelope fields every kind shares — the substrate alone owns the
 * kind/phase/subject vocabulary (`tools/touchpoints.py`), so this type never
 * re-declares it as a closed union.
 */
export interface TouchpointEventInput {
  event_key: string;
  kind: string;
  phase: "arrive" | "leave";
  subject_type: "run" | "brief";
  subject_id: string;
  surface: "outlook_addin" | "ora_web";
  occurred_at: string;
  session_key?: string;
  mailbox_key?: string;
  client_version?: string;
}

export interface RecordTouchpointsRejectedDto {
  index: number;
  reason: string;
}

export interface RecordTouchpointsResponse {
  accepted: number;
  rejected: RecordTouchpointsRejectedDto[];
}

export interface ClientContextSuggestion {
  client_name: string | null;
  policy_number: string | null;
  client_email: string | null;
  recommended_workflow_id: string | null;
  confidence: "high" | "medium" | "low";
  /** SK of the unambiguously matched client — present when the substrate TVF
   *  search returned exactly one result (or a high-confidence rerank pick). */
  client_sk?: number | null;
  /** Full client context pre-fetched by the substrate. When present, the UI
   *  can hydrate directly without extra API calls to /api/clients/{sk}. */
  client_context?: ClientContext | null;
  /** Low-confidence multi-match candidates (mapped from the Product API's
   *  suggested_clients). Present when the substrate could not resolve a single
   *  client — the UI surfaces them as a "did you mean" picker. */
  suggestions?: ClientSearchResult[] | null;
  /** Bounded PDF/attachment text extracted by the substrate during analyze_email.
   *  Stashed by App.tsx and forwarded into triggerPrefill → workflow_prefill so
   *  prefill can read PDF-only identifiers. Not an extraction field — kept out of
   *  the already_extracted payload. */
  attachment_text?: string | null;
  /** `EmailContext.emailId` of the mail item this analysis was run for. Set by
   *  useWorkflowStatus, never by the wire — App.tsx files the stash under it,
   *  so a result that lands after the AM moved on cannot seed another email's
   *  launch (bug 28610). */
  source_email_id?: string | null;
}

/**
 * Response shape from the new sync POST /api/workflows/analyze_email endpoint.
 * `recommended_workflow_id` is promoted to the top level by the Product API
 * handler (it is NOT nested inside `extraction`).
 */
/** snake_case wire shape of a low-confidence client suggestion from
 *  AnalyzeEmailResponse.suggested_clients (Product API). Mapped to the camelCase
 *  ClientSearchResult in useWorkflowStatus before it reaches the UI. */
export interface AnalyzeEmailSuggestedClient {
  client_sk: number | null;
  client_name: string | null;
  branch_name?: string | null;
  city?: string | null;
  state?: string | null;
}

/** Optional live-item fields so re-analysis is not body-empty after discard. */
export interface ReanalyzeEmailFields {
  email_subject?: string;
  email_sender?: string;
  email_body?: string;
  email_rest_id?: string;
}

export interface AnalyzeEmailSyncResponse {
  extraction: {
    client_name: string | null;
    policy_number: string | null;
    client_email: string | null;
    confidence: "high" | "medium" | "low";
  };
  client_context: ClientContext | null;
  recommended_workflow_id: string | null;
  /** Low-confidence multi-match candidates (null/absent on single-match). */
  suggested_clients?: AnalyzeEmailSuggestedClient[] | null;
  /** Bounded PDF/attachment text the substrate extracted from the email. Forwarded
   *  into triggerPrefill → workflow_prefill.attachment_text so prefill reads
   *  PDF-only identifiers. Null/absent when the email had no PDF content. */
  attachment_text?: string | null;
  /** Story 26752 AC-5: "an operator affordance, not something any viewer can
   *  trigger." Computed server-side from the caller's access level (the SAME
   *  check POST reanalyze_email enforces) so App.tsx can withhold `onReanalyse`
   *  from LaunchForm entirely rather than mount a control that would 403 on
   *  press. A CLIENT-SIDE HINT only — absent/false must never be treated as
   *  proof the endpoint would deny; the [Authorize] on the API is the actual
   *  boundary. Missing (older cached response shape) reads as `false` — fail
   *  closed, matching the API's own default. */
  can_reanalyze?: boolean;
}

/**
 * AC-8: final result envelope for a completed workflow instance.
 * Mirrors WorkflowResultResponse from PluginWorkflowController.
 */
export interface WorkflowResultEnvelope {
  instance_id: string;
  status: string;
  completed_at: string | null;
  result: WorkflowResultBody | null;
}

export interface ListInstancesQuery {
  status_filter?: string[];
  limit?: number;
  cursor?: string;
  /** The pane's In-flight list (2026-10-06): rows whose `metadata` is cut to the
   *  oraFix campaign link (all `isCampaignRow` reads). Never for a caller that
   *  matches on metadata anchors — the 409 lookup reads full rows. */
  compact?: boolean;
}

export interface ListInstancesResult {
  items: ActiveWorkflow[];
  next_cursor?: string | null;
}

export interface PrefillResult {
  /**
   * Freeform prefill payload merged into the launch form values.
   *
   * prefill-policy-lookup rides three freeform keys here (not declared in the
   * input schema, so consumers must type-narrow before use):
   *   - `policy_number?: string` — set when exactly one in-force homeowners
   *     policy resolves (AC-2).
   *   - `policy_options?: PolicyInForce[]` — ordered (expiration-desc) list when
   *     more than one qualifies; the add-in renders the dropdown (AC-4).
   *   - `policy_number_resolution?: "inforce_homeowners_single"
   *        | "inforce_homeowners_multiple" | "no_inforce_homeowners_policy"`
   *     — marker the add-in uses to pick the single/multi/none affordance.
   * See components/policyPrefillOptions.ts#readPolicyOptions for the narrowing.
   */
  prefill_values: Record<string, unknown>;
  field_confidence: Record<string, "high" | "medium" | "low">;
}

/**
 * Credential status for a single flow credential owned by the current user.
 * Status values:
 *   "set"           — credential is saved and not stale.
 *   "needs_update"  — credential is saved but has been marked stale (e.g.
 *                     a wrong-password auth failure flagged it).
 *   "not_set"       — no credential has been saved yet.
 *
 * IMPORTANT: This type intentionally has NO `value` member. The substrate
 * never returns the stored secret over this API path.
 */
export interface UserCredentialStatus {
  credential_name: string;
  status: "set" | "needs_update" | "not_set";
  display_name?: string;
  last_updated?: string | null;
}

/**
 * The contract every consumer (real or stub) implements. Adding a new
 * method here MUST be paired with a stubApi counterpart — the
 * stub-parity test enforces it.
 */
/**
 * One Epic SP pool entry as returned by GET /api/Resources/Workflows/sp-pool.
 *
 * This is the PAGE contract (SpPoolEntry in SpPoolRequests.cs), NOT the
 * substrate's raw list_sp_pool row. ListSpPoolQueryHandler already projects and
 * keeps only slots with credentials when the substrate predates the registry
 * status. A current substrate also lists not_registered rows, so an entry
 * existing does NOT mean "provisioned" any more: test `status === "in_rotation"`.
 * There is no has_credentials field on the wire. (Readiness has its own route.)
 *
 * `status` is in_rotation | verifying | awaiting_verification | password_rejected
 * | removing | not_registered, or "in_use" / "available" from an older substrate.
 * NO secret fields.
 */
export interface SpPoolSlot {
  slot?: number;
  email?: string | null;
  status?: string;
}

/** One row of the substrate mailbox-watch registry (mailbox-enrollment). */
export interface WatchedMailbox {
  mailbox_key: string;
  entered_address: string;
  is_watched: boolean;
  enabled_by_oid?: string | null;
  enabled_at?: string | null;
  disabled_by_oid?: string | null;
  disabled_at?: string | null;
  last_verified_at?: string | null;
  statement_version?: string | null;
  /**
   * Intake governance (story 26725) — a watched mailbox no longer implies
   * it is swept. All six fields are optional so an older API payload (no
   * intake governance yet) still type-checks; the pane treats their absence
   * as "no intake affordance to show".
   */
  workflow_id?: string;
  watch_state?: "watching" | "withdrawn";
  intake_mode?: "off" | "suggest" | "auto";
  owning_branch?: string | null;
  /** Server-decided — the add-in must never infer this from a role. */
  can_set_intake_mode?: boolean;
  intake_mode_options?: string[];
  /** Leader governance: the owning branch's region, when known. */
  owning_region?: string | null;
  /** True when this is the caller's own or linked mailbox (scoped reads only). */
  involves_me?: boolean;
}

export interface EnrollableMailbox {
  address: string;
  kind: "personal" | "shared" | string;
}

export interface ListWatchedMailboxesResponse {
  mailboxes: WatchedMailbox[];
  registry_health?: {
    watched_count?: number;
    status?: string | null;
    awaiting_branch_assignment?: string[];
    awaiting_branch_count?: number;
  } | null;
  /** Own mailbox UNION the enrollment allowlist — what set-up may offer. */
  enrollable_mailboxes?: EnrollableMailbox[];
  /**
   * Break-glass governance descriptor — web-only. The add-in never renders
   * a break-glass affordance and does not need to read this; kept optional
   * and ignored here so the shared response type stays a single contract.
   */
  governance?: {
    can_break_glass_branch: string | null;
    can_break_glass_org: boolean;
  };
}

export interface EnrollMailboxResponse extends WatchedMailbox {
  transitioned?: boolean;
  status?: string | null;
}

export interface UnenrollMailboxResponse extends WatchedMailbox {
  status?: string | null;
}

/** Response from PATCH /api/Resources/Workflows/watched-mailboxes/intake-mode. */
/** POST …/watched-mailboxes/link: `linked` is false when the mailbox isn't enrolled. */
export interface LinkMailboxAccessResponse {
  status: string;
  mailbox_key: string;
  linked: boolean;
  newly_linked: boolean;
}

export interface SetMailboxIntakeModeResponse {
  mailbox_key: string;
  intake_mode: "off" | "suggest" | "auto";
  previous_intake_mode?: "off" | "suggest" | "auto" | null;
  authority?: string | null;
  status: string;
}

export interface OrchestratorApi {
  // ---- Auth priming ----
  primeAuth(): Promise<void>;

  // ---- Catalog + instances ----
  /**
   * GET /api/workflows/catalog. `compact` asks for the pane's LIST (2026-10-06):
   * list fields plus `compact: true` on each entry, ~3 KB instead of ~81 KB. A
   * form or a run reads its workflow's full entry through catalogEntryCache.
   * An API that predates compact ignores it and sends full entries, which carry
   * no marker and are used as they are.
   */
  getCatalog(opts?: { compact?: boolean }): Promise<WorkflowCatalogEntry[]>;
  /**
   * GET /api/workflows/catalog/{workflowId} — ONE workflow's FULL entry
   * (2026-10-06). 404 (ApiError.status) when the catalog does not list it, or
   * from an API without the route; catalogEntryCache then reads the full list.
   */
  getCatalogEntry(workflowId: string): Promise<WorkflowCatalogEntry>;
  listInstances(query?: ListInstancesQuery): Promise<ListInstancesResult>;
  getStatus(instanceId: string): Promise<InstanceStatus>;
  getInstanceLogs(instanceId: string): Promise<InstanceLogEntry[]>;
  getPushChannel(instanceId: string): Promise<PushChannelDescriptor>;

  // ---- Pinned pane (story pinned-pane) ----
  /**
   * GET /api/workflows/waiting — the runs waiting on a PERSON, for the caller
   * and nobody else, longest wait first.
   *
   * Takes NO identity argument of any kind and sends none: the substrate tool
   * has no such parameter, the product-API query record has no such property,
   * and the route binds `limit` alone. AC-8's "any identity in the request is
   * ignored" is structural here — there is nothing to send.
   */
  listWaiting(
    limit?: number,
    mailboxAddress?: string | null,
  ): Promise<WaitingForMeResponse>;
  /** `compact` asks for CARDS (no prefill / confidence / provenance) — the pane's
   *  list; a card's click reads the full brief with `getReadyBrief`. */
  listReadyBriefs(limit?: number, opts?: { compact?: boolean }): Promise<ReadyBriefsResponse>;
  /**
   * POST /api/workflows/ready-briefs/detail — ONE brief in full, read when its
   * card is clicked (2026-10-06). Body `{ message_identity, mailbox_address? }`;
   * `mailboxAddress` is the card's triage ROW key. 404 when it was launched or
   * ignored since — the caller shows a notice instead of the form; any other
   * failure opens the form on the card (readyBriefDetail.ts, Ruling R20).
   */
  getReadyBrief(
    messageIdentity: string,
    mailboxAddress?: string | null,
    opts?: { timeoutMs?: number },
  ): Promise<ReadyBriefItem>;
  /**
   * POST /api/workflows/ready-briefs/ignore — hide one prepared brief from
   * THIS caller's ready-to-start list. Body is `{ message_identity }`.
   * Does not delete the analysis and does not hide the card from a colleague.
   */
  ignoreReadyBrief(messageIdentity: string): Promise<{
    status: string;
    internet_message_id: string;
  }>;
  /**
   * POST /api/workflows/instances/{id}/warnings/{warningId}/acknowledge —
   * warning acknowledgement (2026-10-06): mark ONE warning line on a finished
   * run as seen, for everyone. Resolves only with a well-formed acknowledgement;
   * throws on a refusal (ApiError with the HTTP status: 404 a run you cannot
   * see or a line it does not have, 409 not finished, 403 viewing as someone)
   * and on a malformed 200, so a line is never shown as acknowledged without
   * the server's confirmation.
   */
  acknowledgeRunWarning(instanceId: string, warningId: string): Promise<AcknowledgeWarningResult>;
  /**
   * POST /api/workflows/ready-briefs/prefill — cache completed prefill values
   * on the analysis row. Best-effort; a failure must not block the form.
   */
  storeBriefPrefill(
    messageIdentity: string,
    prefillValues: Record<string, unknown>,
    workflowId?: string | null,
    fieldConfidence?: Record<string, "high" | "medium" | "low"> | null,
  ): Promise<{
    status: string;
    internet_message_id: string;
  }>;
  /**
   * GET /api/workflows/waiting-on-me — the count behind the indicator.
   *
   * NO parameters, and none may be added. The substrate derives the caller from
   * the forwarded token and always narrows to their own work, so there is nothing
   * here that could widen or redirect the read.
   */
  getWaitingOnMe(): Promise<WaitingOnMeResponse>;
  /**
   * POST /api/workflows/email-state — the closed triage state of ONE email.
   *
   * The body is `{ message_identity }`, plus an optional `mailbox_address`
   * (story 26727) narrowing ONLY which mailbox's triage record the returned
   * `brief` reflects — never the runs list, and never a caller identity.
   * POST rather than GET because the declared identity is an RFC 5322
   * Message-ID: it routinely carries `<`, `>`, `@` and `+`, and it does not
   * belong in request-line logs.
   */
  getEmailRunState(
    messageIdentity: string,
    mailboxAddress?: string | null,
  ): Promise<EmailRunStateResponse>;
  /**
   * GET /api/workflows/push-channel — the USER-scoped negotiate descriptor
   * (`user-<oid>`). No instance id, no identity argument: the oid comes from
   * the validated token server-side.
   */
  getUserPushChannel(): Promise<PushChannelDescriptor>;
  /**
   * GET /api/Resources/Workflows/watched-mailboxes — mailboxes currently
   * watched that the calling AM may see. Server-derived; never local state.
   */
  listWatchedMailboxes(): Promise<ListWatchedMailboxesResponse>;
  /**
   * POST /api/Resources/Workflows/watched-mailboxes — turn watching ON after
   * a live read probe. Refusals return 400 with a `reason` token.
   */
  enrollMailbox(
    mailboxAddress: string,
    statementVersion?: string,
  ): Promise<EnrollMailboxResponse>;
  /**
   * DELETE /api/Resources/Workflows/watched-mailboxes?mailboxAddress= —
   * turn watching OFF. The registry row is retained.
   */
  unenrollMailbox(mailboxAddress: string): Promise<UnenrollMailboxResponse>;
  /**
   * PATCH /api/Resources/Workflows/watched-mailboxes/intake-mode — change
   * whether a WATCHED mailbox is actually swept. Independent from watching
   * itself: a mailbox can be watched with intake off. Who may call this is
   * decided server-side (`can_set_intake_mode` on the list row) — the add-in
   * never infers permission from a role. Alpha never offers "auto" here.
   * Refusals arrive through the same `ApiError` path as `enrollMailbox`.
   */
  setMailboxIntakeMode(
    mailboxAddress: string,
    mode: "off" | "suggest" | "auto",
  ): Promise<SetMailboxIntakeModeResponse>;
  /**
   * Link the person to the shared mailbox they just opened an email from. The
   * substrate checks the message is in that mailbox and recent (possession is the
   * proof); refusals (`message_not_in_mailbox` / `message_too_old` /
   * `possession_unverifiable`) arrive as ApiError.
   */
  linkMailboxAccess(mailboxAddress: string, messageId: string): Promise<LinkMailboxAccessResponse>;
  joinSignalRGroup(instanceId: string, connectionId: string, hub: string): Promise<void>;
  pingSignalR(instanceId: string, sender: string): Promise<void>;
  cancelInstance(instanceId: string): Promise<CancelInstanceResult>;
  killExecPlaneSession(
    instanceId: string,
    sessionToken?: string,
  ): Promise<{ instance_id: string; session_token: string; killed: boolean }>;
  getArtifacts(instanceId: string): Promise<ArtifactsResponse>;
  getArtifactPayload(
    instanceId: string,
    filename: string,
  ): Promise<{ instance_id: string; filename: string; payload: unknown }>;
  getHitlHistory(instanceId: string): Promise<HitlHistoryResponse>;

  /**
   * GET /api/workflows/instances/{id}/decisions (story 26742, run record).
   * `degraded: true` on the response means the substrate's get_run_decisions
   * tool could not be reached — Product.Api resolves that as a 200 with the
   * marker set, never a thrown error, since the caller (the run-record page)
   * has a graceful HITL-only fallback for exactly this. A genuine
   * transport/HTTP failure reaching Product.Api itself still throws
   * ApiError, same as every other method here.
   */
  getRunDecisions(instanceId: string): Promise<RunDecisionsResponse>;

  /**
   * POST /api/workflows/telemetry/touchpoints (story 26747). Fire-and-forget
   * AT THE SUBSTRATE LAYER: a substrate failure (including the tool not
   * existing yet) still resolves as `accepted: 0`, never an error. A
   * transport/HTTP failure reaching Product.Api itself still throws
   * ApiError — callers that want a true fire-and-forget POST (e.g. a
   * background flush) should catch that themselves, same as any other call.
   */
  recordTouchpoints(events: TouchpointEventInput[]): Promise<RecordTouchpointsResponse>;

  // ---- Feature flags ----
  /**
   * GET /api/FeatureFlag/{key}. Mirrors ora's
   * `featureflag.service.server.ts:12` route shape (`/FeatureFlag/${key}`);
   * the add-in's `request()` base URL does not bake in the `/api` prefix
   * the way ora's `ApiUrlBase` config does, so the add-in path includes it
   * explicitly.
   *
   * Fail-closed: ANY error (network, 401, 404, non-JSON) resolves
   * `{ isEnabled: false }` — never throws into the launch flow.
   */
  getFeatureFlag(key: string): Promise<{ isEnabled: boolean }>;

  // ---- Trigger ----
  triggerWorkflow(
    workflowId: string,
    inputs: TriggerWorkflowInputs,
    metadata?: Record<string, unknown>,
  ): Promise<TriggerWorkflowResult>;

  // ---- Client context ----
  getClientDetails(clientSk: number): Promise<ClientDetails | null>;
  getClientPolicies(clientSk: number): Promise<PolicyInForce[]>;
  /** Legacy search via GET /api/Clients/SearchClientInfo?clientInfo=... */
  searchClientsLegacy(query: string): Promise<import("../types/client").ClientSearchResult[]>;
  /** New search via GET /api/workflows/clients/search?q=... (AC-20). */
  searchClientsViaWorkflows(query: string, limit?: number): Promise<import("../types/client").ClientSearchResult[]>;
  getMyKpis(window: "7d"): Promise<WorkflowKpis>;

  // ---- Legacy endorsement surface (v1 parity) ----
  execute(payload: ExecutePayload): Promise<OrchestratorResponse>;
  approve(workflowId: string, payload: ApprovePayload): Promise<OrchestratorResponse>;
  analyzeEmail(payload: ExecutePayload): Promise<{ instance_id: string }>;
  pollAnalysis(instanceId: string): Promise<ClientContextSuggestion>;
  triggerCarrierWorkflow(
    workflowId: string,
    inputs: Record<string, unknown>,
  ): Promise<{ instance_id: string }>;

  // ---- Sync analyze path (outlook_workflows_e2e_v2 flag ON) ----
  /**
   * Single synchronous POST to `/api/workflows/analyze_email`.
   * Returns `{extraction, client_context, recommended_workflow_id}` at top level.
   * Used when the `outlook_workflows_e2e_v2` feature flag is ON.
   * The legacy two-phase `analyzeEmail` + `pollAnalysis` path is the fallback
   * when the flag is OFF or when this call returns a 5xx.
   */
  analyzeEmailSync(payload: AnalyzeEmailPayload): Promise<AnalyzeEmailSyncResponse>;

  /**
   * RE-ANALYSE — discard the stored analysis for this email and re-derive it.
   *
   * A thin pass-through to `POST /api/workflows/reanalyze_email`, following the
   * sync `analyze_email` endpoint's SHAPE. The server operation belongs to
   * `picks-adoption`; this story owns only the caller.
   *
   * Two things deliberately differ from `analyzeEmailSync`:
   *  - It sends the caller's bearer token like every other MUTATING call here
   *    (`request` always attaches it). `analyze_email` is a cache-and-compute
   *    that runs without one; re-analysis discards a stored analysis and
   *    attributes the new record to a HUMAN, so the caller's identity has to
   *    travel. The actor is derived from that token server-side — never sent
   *    in the body.
   *  - The email is identified by `internet_message_id` (required). Subject /
   *    body / sender / rest id are optional extras so the re-derive has the
   *    live item; Graph only re-reads PDF attachments, not the body.
   */
  reanalyzeEmail(
    internetMessageId: string,
    mailboxAddress?: string | null,
    email?: ReanalyzeEmailFields | null,
  ): Promise<AnalyzeEmailSyncResponse>;

  // ---- AC-8: completion result ----
  /**
   * Fetch the final result envelope for a completed workflow instance.
   * GET /api/workflows/instances/{instanceId}/result
   * Returns `{instance_id, status, completed_at, result}`.
   * Should be called at most once per instance (memoized by caller).
   */
  getWorkflowResult(instanceId: string): Promise<WorkflowResultEnvelope>;

  /**
   * `internetMessageId` / `mailboxAddress` are the email's identity, sent only
   * when non-empty. With a blank `sourceDocumentText` they let workflow_prefill
   * recover the body (Graph) and attachment text (the stored analysis row) —
   * the path for a brief opened from the Today list whose email is not the
   * item selected in Outlook.
   */
  triggerPrefill(
    targetWorkflowId: string,
    clientSk?: number | null,
    sourceDocumentText?: string,
    alreadyExtracted?: Record<string, unknown>,
    attachmentText?: string,
    internetMessageId?: string | null,
    mailboxAddress?: string | null,
  ): Promise<{ instance_id: string }>;

  /**
   * The same prefill run as `triggerPrefill`, with a deliberately FRESH
   * idempotency key.
   *
   * `triggerPrefill` keys on `prefill:${hashPrefillInputs(inputs)}`, so a
   * re-press with unchanged inputs returns the SAME completed instance and the
   * SAME stale values — the RE-ANALYSE button would appear to work and change
   * nothing. That is the silent-success failure the control exists to remove,
   * so the key carries a per-press suffix and nothing else changes.
   *
   * There is NO `force_refresh` flag: nothing reads one (zero occurrences in
   * the substrate), and a prefill-side discard alone would still be wrong here
   * — the add-in forwards its client-side extraction as `already_extracted` and
   * the substrate treats known fields as winning, so a recomputed brief would
   * be overwritten by the stale values the client sent. The caller must refresh
   * its extraction from `reanalyzeEmail` FIRST and forward THAT.
   */
  reanalysePrefill(
    targetWorkflowId: string,
    clientSk?: number | null,
    sourceDocumentText?: string,
    alreadyExtracted?: Record<string, unknown>,
    attachmentText?: string,
    internetMessageId?: string | null,
    mailboxAddress?: string | null,
  ): Promise<{ instance_id: string }>;
  pollPrefill(instanceId: string): Promise<PrefillResult>;
  /**
   * Answer a HITL decision, and RETURN THE ACK.
   *
   * Was `Promise<void>` (emuw-v1-5 `shared-decision-components`). The request
   * already asked for an `OrchestratorResponse` and threw it away, so a warm gate
   * answered elsewhere — HTTP 200, `status: "already_handled"` — looked to this
   * surface exactly like success. Returned UNINTERPRETED; `decisionAnswerOutcome.ts`
   * classifies it.
   *
   * The retry posture is UNCHANGED. This is a side-effecting POST, and making a
   * retried resume more likely would recreate the double-submit this story closes.
   */
  resumeInstance(
    instanceId: string,
    userInput: Record<string, unknown>,
  ): Promise<OrchestratorResponse>;

  /**
   * "Check Now" — trigger an immediate eDocs poll for a parked (pending_edocs)
   * instance. POST /api/workflows/instances/{id}/check-edocs. Returns the poll
   * outcome (released / no_edoc_yet / ambiguous); does NOT resume the workflow.
   */
  checkEdocs(instanceId: string): Promise<{ outcome: string }>;

  /**
   * "Run the sent-reply sweep" - kick off ONE sweep pass instead of waiting for
   * the scheduled job, so a run parked in `pending_email_send` on a local stack
   * can be released. POST /api/workflows/sent-reply-sweep.
   *
   * Takes no instance id BY DESIGN: the substrate route takes none and checks
   * every mailbox with outstanding work, so the counts describe the whole
   * backlog rather than one run. The pane must not word them otherwise.
   */
  runSentReplySweep(): Promise<SentReplySweepOutcome>;

  // ---- Flow credentials (standing self-serve) ----
  /**
   * GET /api/workflows/credentials/status
   * Returns metadata for each flow credential the caller has saved (or not yet
   * saved). The substrate derives the user identity from the forwarded bearer
   * token — no `user_oid` parameter is accepted or returned.
   *
   * IMPORTANT: Response items NEVER contain a `value` member.
   */
  getMyCredentialStatus(): Promise<UserCredentialStatus[]>;

  /**
   * GET /api/workflows/preferences/oversight-level
   * The caller's stored oversight preference (HIGH when never set). The
   * launch form preselects this — the form always submits an explicit level,
   * and the submitted level becomes the standing preference for the next
   * run (substrate write-back). Returns null on any error so the caller
   * falls back to the local default rather than blocking the form.
   */
  getMyOversightLevel(): Promise<OversightLevel | null>;

  /**
   * POST /api/workflows/credentials/update
   * Saves (or overwrites) the named credential for the caller.
   * The `value` is write-only — it is never echoed in the response.
   */
  updateMyFlowPassword(name: string, value: string): Promise<void>;

  /**
   * GET /api/Resources/Workflows/sp-pool
   *
   * NOTE the base: this controller is `api/Resources/[controller]`, which is
   * NOT where the credential endpoints live (`/api/workflows/credentials/*`).
   * Copying the credentials base here returned 404, which fetchSpPoolReady
   * swallowed into "pool empty" — the add-in reported "No service principals
   * in pool" with two provisioned SPs on the admin page (2026-08-18).
   *
   * Returns provisioned slots only; never returns secret values. Admin-gated
   * server-side, so a 403 for a non-admin caller is expected.
   */
  getSpPool(): Promise<SpPoolSlot[]>;

  /**
   * How many SP pool slots are provisioned — the launch-gate question.
   *
   * Distinct from {@link getSpPool} because of AUTHORIZATION, not shape.
   * `getSpPool` is CentralTeamMember-gated (it carries SP login addresses and
   * lease state), so every branch AM probing readiness through it got a 403 and
   * could not launch a pooled-SP run at all. This endpoint is authenticated-only
   * and returns a bare count, so the gate is answerable by whoever can launch.
   *
   * Throws on failure — an unreachable pool is UNKNOWN, not empty. Callers must
   * keep those apart; see `fetchSpPoolReadiness`.
   */
  getSpPoolReadiness(): Promise<number>;
}

// ---------------------------------------------------------------------------
// Real-mode implementation
// ---------------------------------------------------------------------------

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

const realApi: OrchestratorApi = {
  async primeAuth(): Promise<void> {
    await _tokenProvider();
  },

  async getCatalog(opts?: { compact?: boolean }): Promise<WorkflowCatalogEntry[]> {
    const data = await request<{ workflows?: WorkflowCatalogEntry[] } | WorkflowCatalogEntry[]>({
      method: "GET",
      // No cache override, on purpose: the API tags the compact list (W/ ETag,
      // private, no-cache), so the browser revalidates it and an unchanged list
      // costs a 304.
      path: `/api/workflows/catalog${opts?.compact ? "?compact=true" : ""}`,
    });
    if (Array.isArray(data)) return data;
    return data?.workflows ?? [];
  },

  async getCatalogEntry(workflowId: string): Promise<WorkflowCatalogEntry> {
    const data = await request<WorkflowCatalogEntry | null>({
      method: "GET",
      path: `/api/workflows/catalog/${encodeURIComponent(workflowId)}`,
    });
    if (!data || data.id !== workflowId) {
      // A 200 that is not this workflow's entry is not an entry — the caller
      // shows its error, never a form built from something else.
      throw new Error(`catalog entry: the response is not ${workflowId}'s entry`);
    }
    return data;
  },

  async listInstances(query?: ListInstancesQuery): Promise<ListInstancesResult> {
    const params = new URLSearchParams();
    if (query?.status_filter?.length) {
      params.set("status_filter", query.status_filter.join(","));
    }
    if (query?.limit !== undefined) params.set("limit", String(query.limit));
    if (query?.cursor) params.set("cursor", query.cursor);
    if (query?.compact) params.set("compact", "true");
    const qs = params.toString();
    const path = `/api/workflows/instances${qs ? `?${qs}` : ""}`;
    const data = await request<{ items: ActiveWorkflow[]; next_cursor?: string | null }>({
      method: "GET",
      path,
    });
    // oraFix campaigns never appear in the add-in (John, 2026-09-28): every list the
    // add-in renders or counts reads through here. ora's Monitor still shows them.
    const items = (data.items ?? []).filter((row) => !isCampaignRow(row));
    return { items, next_cursor: data.next_cursor ?? null };
  },

  async getStatus(instanceId: string): Promise<InstanceStatus> {
    return request<InstanceStatus>({
      method: "GET",
      path: `/api/workflows/instances/${encodeURIComponent(instanceId)}`,
    });
  },

  async getInstanceLogs(instanceId: string): Promise<InstanceLogEntry[]> {
    const data = await request<{ entries?: InstanceLogEntry[] } | InstanceLogEntry[]>({
      method: "GET",
      path: `/api/workflows/instances/${encodeURIComponent(instanceId)}/logs`,
    });
    if (Array.isArray(data)) return data;
    return data?.entries ?? [];
  },

  async getPushChannel(instanceId: string): Promise<PushChannelDescriptor> {
    return request<PushChannelDescriptor>({
      method: "GET",
      path: `/api/workflows/instances/${encodeURIComponent(instanceId)}/push-channel`,
    });
  },

  // ---- Pinned pane (story pinned-pane) ----

  async listWaiting(
    limit?: number,
    mailboxAddress?: string | null,
  ): Promise<WaitingForMeResponse> {
    // Identity still comes from the forwarded token — mailbox_address only
    // narrows THIS caller's rows to the Outlook session's current mailbox.
    const params = new URLSearchParams();
    if (limit !== undefined) params.set("limit", String(limit));
    if (mailboxAddress) params.set("mailbox_address", mailboxAddress);
    const qs = params.toString() ? `?${params.toString()}` : "";
    const data = await request<WaitingForMeResponse>({
      method: "GET",
      path: `/api/workflows/waiting${qs}`,
    });
    return {
      items: data.items ?? [],
      count: data.count ?? 0,
      needs_you_count: data.needs_you_count ?? 0,
      running_count: data.running_count,
      triaged_today: data.triaged_today ?? 0,
      as_of: data.as_of ?? null,
    };
  },

  async listReadyBriefs(
    limit?: number,
    opts?: { compact?: boolean },
  ): Promise<ReadyBriefsResponse> {
    const params = new URLSearchParams();
    if (limit !== undefined) params.set("limit", String(limit));
    // Cards, not briefs (2026-10-06). An API that predates `compact` ignores it
    // and sends full rows, which render the same and open without a detail read
    // (they carry the prefill_values key; readyBriefDetail.ts, Ruling R22).
    if (opts?.compact) params.set("compact", "true");
    const qs = params.toString() ? `?${params.toString()}` : "";
    const data = await request<ReadyBriefsResponse>({
      method: "GET",
      path: `/api/workflows/ready-briefs${qs}`,
    });
    return {
      items: data.items ?? [],
      count: data.count ?? 0,
      as_of: data.as_of ?? null,
    };
  },

  async ignoreReadyBrief(messageIdentity: string): Promise<{
    status: string;
    internet_message_id: string;
  }> {
    return request<{ status: string; internet_message_id: string }>({
      method: "POST",
      path: "/api/workflows/ready-briefs/ignore",
      body: { message_identity: messageIdentity },
    });
  },

  async acknowledgeRunWarning(
    instanceId: string,
    warningId: string,
  ): Promise<AcknowledgeWarningResult> {
    const data = await request<unknown>({
      method: "POST",
      path: `/api/workflows/instances/${encodeURIComponent(instanceId)}/warnings/${encodeURIComponent(warningId)}/acknowledge`,
      body: {},
      // Idempotent on the server: a hung API must not hold "Acknowledging…" for
      // the default timeout plus a retry; the person can press again.
      noRetry: true,
      timeoutMs: 15_000,
    });
    // Same strictness as ora's client: anything short of a well-formed
    // acknowledgement is a failure, never an "acknowledged" line.
    if (!isAcknowledgement(data)) {
      throw new Error("acknowledge warning: the response is not a well-formed acknowledgement");
    }
    return data;
  },

  async getReadyBrief(
    messageIdentity: string,
    mailboxAddress?: string | null,
    opts?: { timeoutMs?: number },
  ): Promise<ReadyBriefItem> {
    const data = await request<{ item?: ReadyBriefItem }>({
      method: "POST",
      path: "/api/workflows/ready-briefs/detail",
      // A click is waiting on this. Bounded to 8 s and never retried (the default
      // is 30 s plus a second attempt); a timeout opens the form from the card.
      timeoutMs: opts?.timeoutMs ?? READY_BRIEF_DETAIL_TIMEOUT_MS,
      noRetry: true,
      body: {
        message_identity: messageIdentity,
        ...(mailboxAddress ? { mailbox_address: mailboxAddress } : {}),
      },
    });
    if (!data?.item) {
      // A 200 with no brief is not a brief — route it to the caller's fallback.
      throw new Error("ready-brief detail: no item in response");
    }
    return data.item;
  },

  async storeBriefPrefill(
    messageIdentity: string,
    prefillValues: Record<string, unknown>,
    workflowId?: string | null,
    fieldConfidence?: Record<string, "high" | "medium" | "low"> | null,
  ): Promise<{
    status: string;
    internet_message_id: string;
  }> {
    return request<{ status: string; internet_message_id: string }>({
      method: "POST",
      path: "/api/workflows/ready-briefs/prefill",
      body: {
        message_identity: messageIdentity,
        prefill_values: prefillValues,
        ...(workflowId ? { workflow_id: workflowId } : {}),
        // Story 26727 — omitted entirely when the caller passes none, so
        // the wire body stays byte-identical for every existing caller.
        // NO mailbox_address: the substrate's store_brief_prefill tool never
        // declared that parameter (prefill stays imid-wide by design) —
        // sending it produced a pydantic "unexpected keyword argument" 400.
        ...(fieldConfidence && Object.keys(fieldConfidence).length
          ? { field_confidence: fieldConfidence }
          : {}),
      },
    });
  },

  async getWaitingOnMe(): Promise<WaitingOnMeResponse> {
    const data = await request<WaitingOnMeResponse>({
      method: "GET",
      path: "/api/workflows/waiting-on-me",
    });
    // `available` defaults to FALSE, not true, when the field is missing. An
    // unexpected response shape means we cannot say what is waiting, and the
    // indicator must show its degraded state rather than hide — hiding reads as
    // "nothing waiting", which is the silence this story exists to remove.
    return {
      count: data.count ?? 0,
      instance_ids: data.instance_ids ?? [],
      available: data.available === true,
    };
  },

  async getEmailRunState(
    messageIdentity: string,
    mailboxAddress?: string | null,
  ): Promise<EmailRunStateResponse> {
    // The body is the declared identity, plus (story 26727) the mailbox the
    // brief should be read for — a row-key dimension, never a caller identity.
    return request<EmailRunStateResponse>({
      method: "POST",
      path: "/api/workflows/email-state",
      body: {
        message_identity: messageIdentity,
        ...(mailboxAddress ? { mailbox_address: mailboxAddress } : {}),
      },
    });
  },

  async getUserPushChannel(): Promise<PushChannelDescriptor> {
    return request<PushChannelDescriptor>({
      method: "GET",
      path: "/api/workflows/push-channel",
    });
  },

  async joinSignalRGroup(instanceId: string, connectionId: string, hub: string): Promise<void> {
    await request<unknown>({
      method: "POST",
      path: `/api/workflows/instances/${encodeURIComponent(instanceId)}/signalr-join`,
      body: { connectionId, hub },
    });
  },

  async pingSignalR(instanceId: string, sender: string): Promise<void> {
    await request<unknown>({
      method: "POST",
      path: `/api/workflows/instances/${encodeURIComponent(instanceId)}/push-channel/ping`,
      body: { sender },
      noRetry: true,
    });
  },

  async cancelInstance(instanceId: string): Promise<CancelInstanceResult> {
    // Cancel is naturally idempotent — substrate ignores duplicate cancels.
    // We allow retry on 5xx because the HTTP failure could be transient.
    return request<CancelInstanceResult>({
      method: "POST",
      path: `/api/workflows/instances/${encodeURIComponent(instanceId)}/cancel`,
      body: {},
    });
  },

  /**
   * Debug-only — fire DELETE /sessions/{token} against the exec-plane to
   * free a stuck browser session. Equivalent to:
   *   docker exec workflows-substrate-1 curl -X DELETE \
   *     http://execution-plane:9200/sessions/<token>
   * The substrate resolves the token from hitl_question.page_id when
   * `sessionToken` is omitted. Visibility-gated; out-of-scope callers
   * get not_found.
   */
  async killExecPlaneSession(
    instanceId: string,
    sessionToken?: string,
  ): Promise<{ instance_id: string; session_token: string; killed: boolean }> {
    return request<{ instance_id: string; session_token: string; killed: boolean }>({
      method: "POST",
      path: `/api/workflows/instances/${encodeURIComponent(instanceId)}/kill-exec-plane-session`,
      body: sessionToken ? { session_token: sessionToken } : {},
    });
  },

  async getArtifacts(instanceId: string): Promise<ArtifactsResponse> {
    const response = await request<ArtifactsResponse>({
      method: "GET",
      path: `/api/workflows/instances/${encodeURIComponent(instanceId)}/artifacts`,
    });
    // CSV exports are not offered for download (2026-08-13). Filtered at this
    // transport boundary rather than per-surface: five consumers read this
    // method (InstanceDetailDrawer and four call sites in useWorkflowStatus;
    // ArtifactsPanel was removed in story 25240), and every one of them renders from the same array it
    // later indexes via `onArtifactClick(index)`. See artifactFilters.ts for
    // why the shared section component is the wrong place to filter.
    return {
      ...response,
      artifacts: excludeCsvArtifacts(response?.artifacts ?? []),
    };
  },

  async getArtifactPayload(
    instanceId: string,
    filename: string,
  ): Promise<{ instance_id: string; filename: string; payload: unknown }> {
    return request<{ instance_id: string; filename: string; payload: unknown }>({
      method: "GET",
      path: `/api/workflows/instances/${encodeURIComponent(instanceId)}/artifact-payload?filename=${encodeURIComponent(filename)}`,
    });
  },

  async getHitlHistory(instanceId: string): Promise<HitlHistoryResponse> {
    return request<HitlHistoryResponse>({
      method: "GET",
      path: `/api/workflows/instances/${encodeURIComponent(instanceId)}/hitl-history`,
    });
  },

  async getRunDecisions(instanceId: string): Promise<RunDecisionsResponse> {
    return request<RunDecisionsResponse>({
      method: "GET",
      path: `/api/workflows/instances/${encodeURIComponent(instanceId)}/decisions`,
    });
  },

  async recordTouchpoints(
    events: TouchpointEventInput[],
  ): Promise<RecordTouchpointsResponse> {
    return request<RecordTouchpointsResponse>({
      method: "POST",
      path: `/api/workflows/telemetry/touchpoints`,
      body: { events },
      noRetry: true,
    });
  },

  async getFeatureFlag(key: string): Promise<{ isEnabled: boolean }> {
    try {
      const data = await request<{
        featureFlagkey?: string;
        isEnabled?: boolean;
        roles?: string[] | null;
      }>({
        method: "GET",
        path: `/api/FeatureFlag/${encodeURIComponent(key)}`,
      });
      return { isEnabled: data?.isEnabled === true };
    } catch {
      // Fail-closed — never let a flag-read failure block the launch flow.
      return { isEnabled: false };
    }
  },

  async triggerWorkflow(
    workflowId: string,
    inputs: TriggerWorkflowInputs,
    metadata?: Record<string, unknown>,
  ): Promise<TriggerWorkflowResult> {
    // Silent capture happens before POST and never surfaces to the UI.
    // Failures inside collectSilentTriggerFields are swallowed there.
    //
    // WHY explicit inputs override silent capture (the test workaround):
    // The plugin uses Office SSO (WebApplicationInfo) to silently capture the
    // AM's UPN + a delegated Graph token. But Exchange REFUSES to let an end
    // user *sideload* an SSO/permission-requesting add-in ("Sideloading
    // rejected by Exchange") — it can only be ADMIN-deployed (M365 Integrated
    // apps). So a developer/contractor whose machine can't get that admin
    // install (e.g. a 3Cloud-tenant contractor testing against a different
    // mailbox) has no working silent token, and the silently-captured mailbox
    // UPN is the wrong tenant for whatever manual token they DO have — which
    // produced Graph "ErrorInvalidUser" (am_upn=HSIP vs a 3Cloud token).
    // Letting explicit, non-empty inputs win lets such a tester supply matching
    // am_upn + am_graph_access_token (+ message id) by hand and exercise the
    // Graph path without the SSO install. See project-23951 notes.
    //
    // Precedence: silent capture provides DEFAULTS; explicit, NON-EMPTY caller
    // inputs OVERRIDE them. Blank/omitted form fields fall back to silent
    // capture, so production launches (which never fill those fields) are
    // unchanged.
    const silent = await collectSilentTriggerFields();
    const enrichedInputs: Record<string, unknown> = { ...silent };
    for (const [key, value] of Object.entries(inputs)) {
      if (value !== undefined && value !== null && value !== "") {
        enrichedInputs[key] = value;
      }
    }
    // Shared-mailbox launch threading (plan Phase 3 task 2): non-null ONLY
    // when the flag is ON AND a shared/picked target has been confirmed
    // (sharedMailboxTarget store invariant) — so a null target leaves this
    // block a no-op and `body` byte-identical to today (key ABSENT, not null).
    const launchTarget = getSharedMailboxLaunchTarget(); // from sharedMailboxTarget store
    if (launchTarget) {
      enrichedInputs["target_mailbox"] = launchTarget.targetMailbox;
    }
    const body: Record<string, unknown> = {
      workflow_id: workflowId,
      inputs: enrichedInputs,
    };
    if (metadata) body.metadata = metadata;
    return request<TriggerWorkflowResult>({
      method: "POST",
      path: "/api/workflows/trigger",
      body,
    });
  },

  async getClientDetails(clientSk: number): Promise<ClientDetails | null> {
    try {
      return await request<ClientDetails>({
        method: "GET",
        path: `/api/workflows/clients/${encodeURIComponent(String(clientSk))}`,
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },

  async getClientPolicies(clientSk: number): Promise<PolicyInForce[]> {
    try {
      const details = await request<{ policies_in_force?: PolicyInForce[] } | null>({
        method: "GET",
        path: `/api/workflows/clients/${encodeURIComponent(String(clientSk))}`,
      });
      return details?.policies_in_force ?? [];
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return [];
      throw err;
    }
  },

  async searchClientsLegacy(query: string): Promise<import("../types/client").ClientSearchResult[]> {
    if (!query.trim()) return [];
    // API returns EnumerableResponse<ClientsDto> — unwrap .data; fall back to
    // plain array if a future endpoint variant returns one directly.
    const raw = await request<
      | { data: Array<{ clientSk: number; clientName?: string; branchName?: string }> }
      | Array<{ clientSk: number; clientName?: string; branchName?: string }>
    >({
      method: "GET",
      path: `/api/Clients/SearchClientInfo?clientInfo=${encodeURIComponent(query)}`,
    });
    const arr = Array.isArray(raw) ? raw : ((raw as { data?: unknown[] }).data ?? []);
    type RawClient = {
      clientSk: number;
      clientName?: string;
      branchName?: string;
      clientCity?: string;
      clientState?: string;
      clientType?: string;
      clientPostalCode?: string;
      locationName?: string;
      clientEmail?: string;
      clientPhoneNumber?: string;
    };
    // ora's header search reads "unknown" as no value; so does this.
    const known = (v?: string) => (v && v.toLowerCase() !== "unknown" ? v : undefined);
    // Story 28633: keep zip, location, email and phone. ora's client search row
    // (ClientQuicklinkDropdown) shows them, which is how two same-named
    // clients in one branch stop looking like duplicates.
    return (arr as RawClient[]).map((c) => ({
      clientSk: c.clientSk,
      clientName: c.clientName ?? "",
      branchName: c.branchName,
      city: c.clientCity,
      state: c.clientState,
      clientType: c.clientType,
      postalCode: c.clientPostalCode,
      locationName: c.locationName,
      email: known(c.clientEmail),
      phone: known(c.clientPhoneNumber),
    }));
  },

  async searchClientsViaWorkflows(
    query: string,
    limit = 20,
  ): Promise<import("../types/client").ClientSearchResult[]> {
    if (!query.trim()) return [];
    const raw = await request<{
      results: Array<{ client_id?: string; client_name: string; region_name?: string; branch_name?: string }>;
      query: string;
      limit: number;
    }>({
      method: "GET",
      path: `/api/workflows/clients/search?q=${encodeURIComponent(query)}&limit=${limit}`,
    });
    return (raw.results ?? []).map((c, idx) => ({
      clientSk: idx, // substrate returns client_id (string); addin type needs clientSk (number) — use index as fallback
      clientName: c.client_name,
      branchName: c.branch_name,
    }));
  },

  // -------------------------------------------------------------------
  // Legacy endorsement surface — kept for v1 PlanReview parity.
  // -------------------------------------------------------------------

  execute: async (payload) => {
    const result = await request<OrchestratorResponse>({
      method: "POST",
      path: "/api/workflows/trigger",
      body: {
        workflow_id: payload.workflowType ?? "ora_highstreet_production_insights",
        inputs: {
          email_subject: payload.email_subject || payload.emailSubject,
          email_body: payload.email_body || payload.emailBody,
          email_sender: payload.email_sender || payload.emailSender,
          email_id: payload.email_id || payload.emailId,
          email_received_date: payload.email_received_date || payload.emailReceivedDate,
          branch_filter: payload.branch_filter ?? "Highstreet HQ",
        },
        email_id: payload.email_id || payload.emailId,
        email_subject: payload.email_subject || payload.emailSubject,
        email_body: payload.email_body || payload.emailBody,
        email_sender: payload.email_sender || payload.emailSender,
        email_received_date: payload.email_received_date || payload.emailReceivedDate,
      },
    });
    return result;
  },

  approve: async (workflowId, payload) =>
    request<OrchestratorResponse>({
      method: "POST",
      path: `/api/workflows/instances/${encodeURIComponent(workflowId)}/resume`,
      body: {
        user_input: {
          approved: payload.approved,
          corrections: payload.corrections,
          rejection_reason: payload.rejection_reason ?? payload.rejectionReason,
        },
      },
    }),

  async analyzeEmail(payload: ExecutePayload): Promise<{ instance_id: string }> {
    const result = await request<OrchestratorResponse>({
      method: "POST",
      path: "/api/workflows/trigger",
      body: {
        workflow_id: "analyze_email",
        inputs: {
          email_body: payload.email_body || payload.emailBody,
          email_subject: payload.email_subject || payload.emailSubject,
          email_sender: payload.email_sender || payload.emailSender,
          email_attachments_hint: (payload.attachments ?? []).map((a) => a.name).join(", "),
        },
      },
    });
    return { instance_id: result.instance_id ?? "" };
  },

  async pollAnalysis(instanceId: string): Promise<ClientContextSuggestion> {
    // Match the backend ANALYZE_EMAIL_DEADLINE_SECONDS (300s). Subtract 10s
    // to ensure we emit poll_timeout before the substrate cancels the instance.
    const MAX_POLL_MS = PREFILL_POLL_TIMEOUT_MS;
    // Per-request timeout matches the default (30s) to accommodate the
    // OBO token fetch + MCP initialize handshake + tools/call round-trip.
    const POLL_REQUEST_TIMEOUT_MS = 30_000;
    const started = Date.now();

    for (;;) {
      if (Date.now() - started > MAX_POLL_MS) {
        throw new Error("poll_timeout");
      }

      type StatusWithMeta = OrchestratorResponse & {
        metadata?: {
          extraction?: Omit<ClientContextSuggestion, "client_sk" | "client_context">;
          client_context?: ClientContext | null;
        };
      };
      let s: StatusWithMeta;
      try {
        s = await request<StatusWithMeta>({
          method: "GET",
          path: `/api/workflows/instances/${encodeURIComponent(instanceId)}`,
          timeoutMs: POLL_REQUEST_TIMEOUT_MS,
        });
      } catch {
        // Transient network error (timeout, abort, temporary API unavailability).
        // The overall poll has its own MAX_POLL_MS ceiling — just retry.
        await wait(3000);
        continue;
      }
      if (s.status === "completed") {
        const extraction = s.metadata?.extraction ?? {
          client_name: null,
          policy_number: null,
          client_email: null,
          recommended_workflow_id: null,
          confidence: "low" as const,
        };
        const clientCtx = s.metadata?.client_context ?? null;
        return {
          ...extraction,
          client_sk: clientCtx?.client?.client_sk ?? null,
          client_context: clientCtx,
        };
      }
      if (s.status === "failed") throw new Error(s.error ?? "Email analysis failed");
      if (s.status === "timeout_cancelled" || s.status === "cancelled") {
        throw new Error(`Email analysis ${s.status.replace("_", " ")} — please try again`);
      }
      await wait(2000);
    }
  },

  async triggerCarrierWorkflow(
    workflowId: string,
    inputs: Record<string, unknown>,
  ): Promise<{ instance_id: string }> {
    // carrier_mortgagee_update post-portal flow needs these fields — capture
    // is silent, additive, and tolerant of any individual capture failure.
    const silent = await collectSilentTriggerFields();
    const result = await request<OrchestratorResponse>({
      method: "POST",
      path: "/api/workflows/trigger",
      body: { workflow_id: workflowId, inputs: { ...inputs, ...silent } },
    });
    return { instance_id: result.instance_id ?? "" };
  },

  async analyzeEmailSync(payload: AnalyzeEmailPayload): Promise<AnalyzeEmailSyncResponse> {
    return request<AnalyzeEmailSyncResponse>({
      method: "POST",
      path: "/api/workflows/analyze_email",
      body: {
        email_subject: payload.email_subject,
        email_body: payload.email_body,
        email_sender: payload.email_sender,
        email_id: payload.email_id,
        email_received_date: payload.email_received_date,
        // REST message id + mailbox let the substrate read the email's PDF
        // attachments via Graph (the add-in doesn't send attachment bytes);
        // internet_message_id is the retrieve-first cache key. All three are
        // omitted when absent so the wire body stays clean.
        ...(payload.email_rest_id ? { email_rest_id: payload.email_rest_id } : {}),
        ...(payload.internet_message_id ? { internet_message_id: payload.internet_message_id } : {}),
        ...(payload.mailbox_address ? { mailbox_address: payload.mailbox_address } : {}),
        ...(payload.skip_workflow_recommendation
          ? { skip_workflow_recommendation: true }
          : {}),
        ...(payload.selected_workflow_id
          ? { selected_workflow_id: payload.selected_workflow_id }
          : {}),
      },
      // analyze_email runs an LLM extraction + Lakebase client lookup on the
      // substrate — and, when the email has a PDF, a Graph fetch + PDF OCR
      // (vision, locally). Cold-path latency (client-lookup Databricks cascade
      // + scanned-PDF OCR) can approach a minute in dev, so allow 120s to avoid
      // an AbortError while the backend is still finishing. AC-2's <2s SLA is a
      // substrate-side performance goal; this frontend timeout is only a backstop.
      // Must stay <= the API's McpSubstrate:TimeoutSeconds so the API doesn't
      // abort first.
      timeoutMs: 120_000,
      // noRetry: false — the substrate is idempotent for analyze_email;
      // allow the built-in retry-once on 5xx so transient errors are handled.
    });
  },

  async getWorkflowResult(instanceId: string): Promise<WorkflowResultEnvelope> {
    return request<WorkflowResultEnvelope>({
      method: "GET",
      path: `/api/workflows/instances/${encodeURIComponent(instanceId)}/result`,
    });
  },

  async reanalyzeEmail(
    internetMessageId: string,
    mailboxAddress?: string | null,
    email?: ReanalyzeEmailFields | null,
  ): Promise<AnalyzeEmailSyncResponse> {
    return request<AnalyzeEmailSyncResponse>({
      method: "POST",
      path: "/api/workflows/reanalyze_email",
      body: {
        internet_message_id: internetMessageId,
        // Omitted when absent so the wire body stays clean — the same rule
        // analyzeEmailSync follows for its optional identifiers.
        ...(mailboxAddress ? { mailbox_address: mailboxAddress } : {}),
        ...(email?.email_subject ? { email_subject: email.email_subject } : {}),
        ...(email?.email_sender ? { email_sender: email.email_sender } : {}),
        ...(email?.email_body ? { email_body: email.email_body } : {}),
        ...(email?.email_rest_id ? { email_rest_id: email.email_rest_id } : {}),
      },
      // Re-analysis re-runs the same LLM extraction + client lookup (and the
      // Graph PDF read) analyze_email does, so it inherits its cold-path
      // budget rather than the fast-call default.
      timeoutMs: 120_000,
      // The caller pressed a button and is watching: a silent retry would
      // start a SECOND re-analysis of the same email, which is exactly what
      // the control's single-flight guard exists to prevent client-side.
      noRetry: true,
    });
  },

  async triggerPrefill(
    targetWorkflowId: string,
    clientSk?: number | null,
    sourceDocumentText?: string,
    alreadyExtracted?: Record<string, unknown>,
    attachmentText?: string,
    internetMessageId?: string | null,
    mailboxAddress?: string | null,
  ): Promise<{ instance_id: string }> {
    const inputs = buildPrefillInputs(
      targetWorkflowId,
      clientSk,
      sourceDocumentText,
      alreadyExtracted,
      attachmentText,
      internetMessageId,
      mailboxAddress,
    );
    const result = await request<OrchestratorResponse>({
      method: "POST",
      path: "/api/workflows/trigger",
      body: {
        workflow_id: "workflow_prefill",
        inputs,
        // Prefill is a pure function of its inputs, so identical inputs reuse
        // the prior COMPLETED instance via the substrate's idempotency store —
        // reopening the drawer polls that instance and gets prefill_values
        // instantly instead of re-running the LLM extraction. Any input change
        // (client pick, chosen policy) hashes to a new key and runs fresh.
        idempotency_key: `prefill:${hashPrefillInputs(inputs)}`,
      },
    });
    return { instance_id: result.instance_id ?? "" };
  },

  async reanalysePrefill(
    targetWorkflowId: string,
    clientSk?: number | null,
    sourceDocumentText?: string,
    alreadyExtracted?: Record<string, unknown>,
    attachmentText?: string,
    internetMessageId?: string | null,
    mailboxAddress?: string | null,
  ): Promise<{ instance_id: string }> {
    const inputs = buildPrefillInputs(
      targetWorkflowId,
      clientSk,
      sourceDocumentText,
      alreadyExtracted,
      attachmentText,
      internetMessageId,
      mailboxAddress,
    );
    const result = await request<OrchestratorResponse>({
      method: "POST",
      path: "/api/workflows/trigger",
      body: {
        workflow_id: "workflow_prefill",
        inputs,
        // The ONE difference from triggerPrefill, and the reason this method
        // exists: the key is FRESH per press. Reusing the hashed key would
        // return the prior COMPLETED instance with the prior values — a
        // RE-ANALYSE that appears to work and changes nothing.
        idempotency_key: `prefill:${hashPrefillInputs(inputs)}:reanalyse:${Date.now()}`,
      },
    });
    return { instance_id: result.instance_id ?? "" };
  },

  async pollPrefill(instanceId: string): Promise<PrefillResult> {
    const POLL_REQUEST_TIMEOUT_MS = 30_000;
    const started = Date.now();
    for (;;) {
      if (Date.now() - started > PREFILL_POLL_TIMEOUT_MS) {
        return { prefill_values: {}, field_confidence: {} };
      }
      type StatusWithPrefill = OrchestratorResponse & {
        metadata?: {
          prefill_values?: Record<string, unknown>;
          field_confidence?: Record<string, string>;
        };
      };
      let s: StatusWithPrefill;
      try {
        s = await request<StatusWithPrefill>({
          method: "GET",
          path: `/api/workflows/instances/${encodeURIComponent(instanceId)}`,
          timeoutMs: POLL_REQUEST_TIMEOUT_MS,
        });
      } catch {
        await wait(3000);
        continue;
      }
      if (s.status === "completed") {
        const prefill_values = s.metadata?.prefill_values ?? {};
        const raw_confidence = s.metadata?.field_confidence ?? {};
        const field_confidence: Record<string, "high" | "medium" | "low"> = {};
        for (const [k, v] of Object.entries(raw_confidence)) {
          if (v === "high" || v === "medium" || v === "low") {
            field_confidence[k] = v;
          }
        }
        return { prefill_values, field_confidence };
      }
      if (
        s.status === "failed" ||
        s.status === "timeout_cancelled" ||
        s.status === "cancelled"
      ) {
        return { prefill_values: {}, field_confidence: {} };
      }
      await wait(2000);
    }
  },

  async resumeInstance(
    instanceId: string,
    userInput: Record<string, unknown>,
  ): Promise<OrchestratorResponse> {
    // Same call, same options, same retry posture — only the value is no longer
    // discarded. Do not add `noRetry` here: changing the retry behaviour of a
    // side-effecting POST is exactly the double-submit risk this story closes.
    return await request<OrchestratorResponse>({
      method: "POST",
      path: `/api/workflows/instances/${encodeURIComponent(instanceId)}/resume`,
      body: { user_input: userInput },
    });
  },

  async checkEdocs(instanceId: string): Promise<{ outcome: string }> {
    // Side-effecting POST with no idempotency key — disable retry so a transient
    // 5xx doesn't fire the poll twice (the substrate poll is idempotent, but a
    // duplicate release event is wasteful).
    const result = await request<{ outcome?: string }>({
      method: "POST",
      path: `/api/workflows/instances/${encodeURIComponent(instanceId)}/check-edocs`,
      body: {},
      noRetry: true,
    });
    return { outcome: result?.outcome ?? "no_edoc_yet" };
  },

  async runSentReplySweep(): Promise<SentReplySweepOutcome> {
    // Side-effecting POST, no idempotency key -> noRetry, exactly as checkEdocs
    // does. The sweep itself IS idempotent (matched/expired rows are never
    // reprocessed), but a retried pass doubles the Graph traffic for nothing.
    const result = await request<{ pending?: number; mailboxes?: number; runId?: string; run_id?: string }>({
      method: "POST",
      path: "/api/workflows/sent-reply-sweep",
      body: {},
      noRetry: true,
    });
    return {
      pending: result?.pending ?? 0,
      mailboxes: result?.mailboxes ?? 0,
      // The .NET record serialises as `runId`; the substrate's own envelope
      // says `run_id`. Accept both so a future passthrough change cannot
      // silently blank the one handle on per-instance outcomes.
      runId: result?.runId ?? result?.run_id ?? "",
    };
  },

  async getMyKpis(window: "7d"): Promise<WorkflowKpis> {
    return await request<WorkflowKpis>({
      method: "GET",
      path: `/api/workflows/kpis?window=${window}`,
    });
  },

  async getMyCredentialStatus(): Promise<UserCredentialStatus[]> {
    const data = await request<{ credentials?: UserCredentialStatus[] } | UserCredentialStatus[]>({
      method: "GET",
      path: "/api/workflows/credentials/status",
    });
    if (Array.isArray(data)) return data;
    return data?.credentials ?? [];
  },

  async getSpPool(): Promise<SpPoolSlot[]> {
    // The response envelope is `{ entries: [...] }` (ListSpPoolResponse).
    // Array + `slots` are tolerated only so a shape change degrades to a
    // readable list rather than a silent empty one.
    const data = await request<
      { entries?: SpPoolSlot[]; slots?: SpPoolSlot[] } | SpPoolSlot[]
    >({
      method: "GET",
      path: "/api/Resources/Workflows/sp-pool",
    });
    if (Array.isArray(data)) return data;
    return data?.entries ?? data?.slots ?? [];
  },

  async getSpPoolReadiness(): Promise<number> {
    // Envelope is `{ provisioned: N }` (SpPoolReadinessResponse). A bare number
    // is tolerated so a shape change degrades to a real count, not a silent 0 —
    // 0 is a MEANINGFUL value here (pool empty) and must not be a parse default.
    const data = await request<{ provisioned?: number } | number>({
      method: "GET",
      path: "/api/Resources/Workflows/sp-pool/readiness",
    });
    const n = typeof data === "number" ? data : data?.provisioned;
    if (typeof n !== "number" || !Number.isFinite(n)) {
      // Unparseable is UNKNOWN. Throwing routes it to the caller's error branch
      // rather than asserting an empty pool the operator would be sent to fix.
      throw new Error("sp-pool readiness: no `provisioned` count in response");
    }
    return n;
  },

  async getMyOversightLevel(): Promise<OversightLevel | null> {
    try {
      const data = await request<{ oversightLevel?: string | null; oversight_level?: string | null }>({
        method: "GET",
        path: "/api/workflows/preferences/oversight-level",
      });
      const raw = (data?.oversightLevel ?? data?.oversight_level ?? "").toUpperCase();
      return raw === "HIGH" || raw === "MEDIUM" || raw === "LOW" || raw === "NONE"
        ? (raw as OversightLevel)
        : null;
    } catch {
      return null; // fall back to the local default; never block the form
    }
  },

  async updateMyFlowPassword(name: string, value: string): Promise<void> {
    await request<unknown>({
      method: "POST",
      path: "/api/workflows/credentials/update",
      body: { name, value },
      // POST is write-only — not idempotent without a key, so disable retry
      // to avoid double-write risk.
      noRetry: true,
    });
  },

  async listWatchedMailboxes(): Promise<ListWatchedMailboxesResponse> {
    const data = await request<ListWatchedMailboxesResponse>({
      method: "GET",
      path: "/api/Resources/Workflows/watched-mailboxes",
    });
    return {
      mailboxes: data?.mailboxes ?? [],
      registry_health: data?.registry_health ?? null,
      enrollable_mailboxes: data?.enrollable_mailboxes ?? [],
    };
  },

  async enrollMailbox(
    mailboxAddress: string,
    statementVersion?: string,
  ): Promise<EnrollMailboxResponse> {
    return request<EnrollMailboxResponse>({
      method: "POST",
      path: "/api/Resources/Workflows/watched-mailboxes",
      body: {
        mailbox_address: mailboxAddress,
        ...(statementVersion ? { statement_version: statementVersion } : {}),
      },
      noRetry: true,
    });
  },

  async unenrollMailbox(mailboxAddress: string): Promise<UnenrollMailboxResponse> {
    const qs = new URLSearchParams({ mailboxAddress });
    return request<UnenrollMailboxResponse>({
      method: "DELETE",
      path: `/api/Resources/Workflows/watched-mailboxes?${qs.toString()}`,
      noRetry: true,
    });
  },

  async setMailboxIntakeMode(
    mailboxAddress: string,
    mode: "off" | "suggest" | "auto",
  ): Promise<SetMailboxIntakeModeResponse> {
    return request<SetMailboxIntakeModeResponse>({
      method: "PATCH",
      path: "/api/Resources/Workflows/watched-mailboxes/intake-mode",
      body: {
        mailbox_address: mailboxAddress,
        mode,
      },
      noRetry: true,
    });
  },

  async linkMailboxAccess(mailboxAddress: string, messageId: string): Promise<LinkMailboxAccessResponse> {
    return request<LinkMailboxAccessResponse>({
      method: "POST",
      path: "/api/Resources/Workflows/watched-mailboxes/link",
      body: {
        mailbox_address: mailboxAddress,
        message_id: messageId,
      },
      noRetry: true,
    });
  },
};

// ---------------------------------------------------------------------------
// Stub implementation — every realApi method gets a counterpart.
// ---------------------------------------------------------------------------

function stubPlan(payload: ExecutePayload): Record<string, unknown> {
  return {
    policy_number: {
      value: "AUTO-2025-447821",
      confidence: 0.92,
      source: "email-subject",
      needs_am_input: false,
    },
    client_name: {
      value: (payload.email_sender || payload.emailSender || "")
        .split("@")[0]
        .replace(/[._]/g, " "),
      confidence: 0.81,
      source: "epic-lookup",
      needs_am_input: false,
    },
    line_of_business: {
      value: "Personal Auto",
      confidence: 0.88,
      source: "epic-lookup",
      needs_am_input: false,
    },
    change_type: {
      value: "Address change",
      confidence: 0.74,
      source: "llm-parse",
      needs_am_input: false,
    },
    effective_date: {
      value: new Date(Date.now() + 7 * 86_400_000).toISOString().slice(0, 10),
      confidence: 0.55,
      source: "llm-parse",
      needs_am_input: true,
    },
    carrier: {
      value: "Travelers",
      confidence: 0.9,
      source: "epic-lookup",
      needs_am_input: false,
    },
    change_details: (payload.email_body || payload.emailBody || "").slice(0, 280),
    epic_client_id: "EPIC-883301",
    assigned_am: "Joachim Landes",
    current_coverage_summary: "100/300/100 BI/PD, $500 deductible.",
    carrier_portal_url: "https://example.travelers.com/policy/AUTO-2025-447821",
  };
}

const stubApi: OrchestratorApi = {
  async primeAuth(): Promise<void> {
    // Stub mode skips auth entirely.
  },

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- arity parity with the real api (stubApi parity test)
  async getCatalog(_opts?: { compact?: boolean }): Promise<WorkflowCatalogEntry[]> {
    await wait(150);
    // Full entries with no compact marker, as an older API sends them — the
    // drawer uses them as they are and never calls getCatalogEntry.
    return DEMO_CATALOG;
  },

  async getCatalogEntry(workflowId: string): Promise<WorkflowCatalogEntry> {
    await wait(60);
    const entry = DEMO_CATALOG.find((e) => e.id === workflowId);
    if (!entry) throw new Error(`stub catalog: no workflow ${workflowId}`);
    return entry;
  },

  async listInstances(_query?: ListInstancesQuery): Promise<ListInstancesResult> {
    await wait(120);
    let items = DEMO_ACTIVE_WORKFLOWS;
    if (_query?.status_filter?.length) {
      const allowed = new Set(_query.status_filter);
      items = items.filter((i) => allowed.has(i.status));
    }
    if (_query?.limit !== undefined) items = items.slice(0, _query.limit);
    return { items, next_cursor: null };
  },

  async getStatus(instanceId: string): Promise<InstanceStatus> {
    await wait(80);
    const found = DEMO_INSTANCE_STATUSES[instanceId];
    if (found) return found;
    // Fallback for arbitrary stub instance ids — synthesize a running state.
    return {
      instance_id: instanceId,
      workflow_id: "mortgagee_update_v1_2",
      status: "running",
      current_phase: "draft",
      created_at: new Date(Date.now() - 30_000).toISOString(),
      updated_at: new Date().toISOString(),
    };
  },

  async getInstanceLogs(instanceId: string): Promise<InstanceLogEntry[]> {
    await wait(60);
    return DEMO_INSTANCE_LOGS[instanceId] ?? [];
  },

  async getPushChannel(_instanceId: string): Promise<PushChannelDescriptor> {
    await wait(40);
    return DEMO_PUSH_CHANNEL;
  },

  // ---- Pinned pane (story pinned-pane) ----
  // DETERMINISTIC fixtures: the add-in defaults to stub mode, so a
  // browser-opened pane must exercise the queue and EVERY strip state without
  // a live stack. Same input, same output, every time.

  async listWaiting(
    limit?: number,
    _mailboxAddress?: string | null,
  ): Promise<WaitingForMeResponse> {
    await wait(60);
    const items =
      limit === undefined
        ? DEMO_WAITING_QUEUE.items
        : DEMO_WAITING_QUEUE.items.slice(0, limit);
    // The counts stay the SERVER's, unchanged by the page size — the badge is
    // never items.length (design Constraint 19).
    return { ...DEMO_WAITING_QUEUE, items };
  },

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- arity parity with the real api (stubApi parity test)
  async listReadyBriefs(limit?: number, _opts?: { compact?: boolean }): Promise<ReadyBriefsResponse> {
    await wait(60);
    const items = [
      {
        internet_message_id: "<brief-demo@example.com>",
        subject: "Mortgagee change — Delgado",
        sender: "lender@example.com",
        client_name: "Delgado",
        policy_number: "HP-4471822",
        recommended_workflow_id: "carrier_mortgagee_update_v2",
        prepared_at: new Date().toISOString(),
        prepared_by: "ambient_intake",
        source_mailbox: "shared@example.com",
        graph_message_id: "AAMkDemoGraphId",
      },
    ];
    return {
      items: limit === undefined ? items : items.slice(0, limit),
      count: 1,
      as_of: new Date().toISOString(),
    };
  },

  async ignoreReadyBrief(messageIdentity: string): Promise<{
    status: string;
    internet_message_id: string;
  }> {
    await wait(40);
    return { status: "ok", internet_message_id: messageIdentity };
  },

  async acknowledgeRunWarning(
    instanceId: string,
    warningId: string,
  ): Promise<AcknowledgeWarningResult> {
    await wait(40);
    const at = new Date().toISOString();
    return {
      instance_id: instanceId,
      warning_id: warningId,
      acknowledged_by_name: "Demo User",
      acknowledged_at: at,
      already_acknowledged: false,
      warning_items: [],
      needs_acknowledgement: false,
    };
  },

  async getReadyBrief(
    messageIdentity: string,
    mailboxAddress?: string | null,
    opts?: { timeoutMs?: number },
  ): Promise<ReadyBriefItem> {
    void opts; // arity parity with the real API; the stub never times out
    await wait(60);
    // The full brief behind the stub's one Ready-to-start card.
    return {
      internet_message_id: messageIdentity,
      subject: "Mortgagee change — Delgado",
      sender: "lender@example.com",
      client_name: "Delgado",
      policy_number: "HP-4471822",
      recommended_workflow_id: "carrier_mortgagee_update_v2",
      prepared_at: new Date().toISOString(),
      prepared_by: "ambient_intake",
      source_mailbox: "shared@example.com",
      mailbox_address: mailboxAddress ?? "shared@example.com",
      graph_message_id: "AAMkDemoGraphId",
      triage_outcome: "matched",
      prefill_values: { policy_number: "HP-4471822", change_type: "add" },
      field_confidence: { policy_number: "high", change_type: "medium" },
    };
  },

  async storeBriefPrefill(
    messageIdentity: string,
    _prefillValues: Record<string, unknown>,
    _workflowId?: string | null,
    _fieldConfidence?: Record<string, "high" | "medium" | "low"> | null,
  ): Promise<{
    status: string;
    internet_message_id: string;
  }> {
    await wait(40);
    return { status: "ok", internet_message_id: messageIdentity };
  },

  async getWaitingOnMe(): Promise<WaitingOnMeResponse> {
    await wait(40);
    // Matches the demo queue's notifiable count so the stubbed pane and the
    // stubbed indicator cannot disagree with each other on screen.
    return {
      count: DEMO_WAITING_QUEUE.needs_you_count,
      instance_ids: DEMO_WAITING_QUEUE.items.map((i) => i.instance_id),
      available: true,
    };
  },

  async getEmailRunState(
    messageIdentity: string,
    _mailboxAddress?: string | null,
  ): Promise<EmailRunStateResponse> {
    await wait(60);
    return demoEmailRunStateFor(messageIdentity);
  },

  async getUserPushChannel(): Promise<PushChannelDescriptor> {
    await wait(40);
    return DEMO_USER_PUSH_CHANNEL;
  },

  async joinSignalRGroup(_instanceId: string, _connectionId: string, _hub: string): Promise<void> {
    // No-op in stub mode — no emulator running.
  },

  async pingSignalR(_instanceId: string, _sender: string): Promise<void> {
    // No-op in stub mode — no emulator running.
  },

  async cancelInstance(instanceId: string): Promise<CancelInstanceResult> {
    await wait(180);
    return { instance_id: instanceId, status: "cancelled" };
  },

  async killExecPlaneSession(
    instanceId: string,
    sessionToken?: string,
  ): Promise<{ instance_id: string; session_token: string; killed: boolean }> {
    await wait(80);
    return {
      instance_id: instanceId,
      session_token: sessionToken ?? "stub-session",
      killed: true,
    };
  },

  async getArtifacts(instanceId: string): Promise<ArtifactsResponse> {
    await wait(80);
    return { instance_id: instanceId, artifacts: [] };
  },

  async getArtifactPayload(
    instanceId: string,
    filename: string,
  ): Promise<{ instance_id: string; filename: string; payload: unknown }> {
    await wait(40);
    return { instance_id: instanceId, filename, payload: {} };
  },

  async getHitlHistory(instanceId: string): Promise<HitlHistoryResponse> {
    await wait(60);
    return { instance_id: instanceId, events: [] };
  },

  async getRunDecisions(instanceId: string): Promise<RunDecisionsResponse> {
    await wait(60);
    return {
      run_id: instanceId,
      workflow_id: null,
      status: null,
      summary: null,
      decisions: [],
      hitl_answers: [],
      notifications: [],
      touchpoints: null,
      counts: null,
      degraded: false,
    };
  },

  async recordTouchpoints(
    events: TouchpointEventInput[],
  ): Promise<RecordTouchpointsResponse> {
    await wait(10);
    return { accepted: events.length, rejected: [] };
  },

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async getFeatureFlag(_key: string): Promise<{ isEnabled: boolean }> {
    await wait(20);
    return { isEnabled: resolveStubSharedMailboxFlag() };
  },

  async triggerWorkflow(
    workflowId: string,
    inputs: TriggerWorkflowInputs,
    _metadata?: Record<string, unknown>,
  ): Promise<TriggerWorkflowResult> {
    await wait(220);
    return {
      instance_id: `stub-${workflowId}-${inputs.idempotency_key ?? Date.now()}`,
      workflow_id: workflowId,
      major_version: 1,
      status: workflowId === "analyze_email" ? "running" : "running",
      warning: false,
    };
  },

  async getClientDetails(clientSk: number): Promise<ClientDetails | null> {
    await wait(120);
    if (clientSk === DEMO_CLIENT_DETAILS.client.client_sk) {
      return DEMO_CLIENT_DETAILS;
    }
    return null;
  },

  async getClientPolicies(clientSk: number): Promise<PolicyInForce[]> {
    await wait(100);
    return DEMO_POLICIES_BY_CLIENT_SK[clientSk] ?? [];
  },

  async searchClientsLegacy(query: string): Promise<import("../types/client").ClientSearchResult[]> {
    await wait(300);
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    const demo = DEMO_CLIENT_DETAILS.client;
    const candidates: import("../types/client").ClientSearchResult[] = [
      { clientSk: demo.client_sk, clientName: demo.client_name, branchName: demo.branch_name },
    ];
    return candidates.filter((c) => c.clientName.toLowerCase().includes(q));
  },

  async searchClientsViaWorkflows(
    query: string,
    _limit = 20,
  ): Promise<import("../types/client").ClientSearchResult[]> {
    await wait(300);
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    const demo = DEMO_CLIENT_DETAILS.client;
    const candidates: import("../types/client").ClientSearchResult[] = [
      { clientSk: demo.client_sk, clientName: demo.client_name, branchName: demo.branch_name },
    ];
    return candidates.filter((c) => c.clientName.toLowerCase().includes(q));
  },

  // -------------------------------------------------------------------
  // Legacy endorsement surface (stub).
  // -------------------------------------------------------------------

  async execute(payload: ExecutePayload): Promise<OrchestratorResponse> {
    await wait(1800);
    return {
      instance_id: `stub-${Date.now()}`,
      workflow_id: `stub-${Date.now()}`,
      status: "pending_plan_review",
      plan: stubPlan(payload),
    };
  },

  async approve(
    workflowId: string,
    payload: ApprovePayload,
  ): Promise<OrchestratorResponse> {
    await wait(1200);
    if (!payload.approved) {
      return { instance_id: workflowId, workflow_id: workflowId, status: "rejected" };
    }
    return {
      instance_id: workflowId,
      workflow_id: workflowId,
      status: "completed",
      execution_time_seconds: 12,
    };
  },

  async analyzeEmail(_payload: ExecutePayload): Promise<{ instance_id: string }> {
    await wait(120);
    return { instance_id: `stub-analyze-${Date.now()}` };
  },

  async pollAnalysis(_instanceId: string): Promise<ClientContextSuggestion> {
    await wait(1200);
    return {
      client_name: "Smith, John",
      policy_number: "TRV-2024-447821",
      client_email: null,
      recommended_workflow_id: "carrier_mortgagee_update_v2",
      confidence: "high",
    };
  },

  async triggerCarrierWorkflow(
    workflowId: string,
    _inputs: Record<string, unknown>,
  ): Promise<{ instance_id: string }> {
    await wait(220);
    return { instance_id: `stub-${workflowId}-${Date.now()}` };
  },

  async analyzeEmailSync(_payload: AnalyzeEmailPayload): Promise<AnalyzeEmailSyncResponse> {
    await wait(400);
    return {
      extraction: {
        client_name: "Smith, John",
        policy_number: "TRV-2024-447821",
        client_email: null,
        confidence: "high",
      },
      client_context: null,
      recommended_workflow_id: "carrier_mortgagee_update_v2",
    };
  },

  async reanalyzeEmail(
    internetMessageId: string,
    mailboxAddress?: string | null,
    email?: ReanalyzeEmailFields | null,
  ): Promise<AnalyzeEmailSyncResponse> {
    // The `stubApi parity` test asserts stub and real have MATCHING ARITIES,
    // so the ignored parameters have to be declared. They are acknowledged
    // rather than `_`-prefixed because this project's flat ESLint config
    // reports unused args whatever they are named.
    void internetMessageId;
    void mailboxAddress;
    void email;
    await wait(400);
    // Deliberately DIFFERENT from analyzeEmailSync's stub: the stub pane must
    // show the AM something changed, or RE-ANALYSE reads as a no-op there.
    return {
      extraction: {
        client_name: "Smith, John",
        policy_number: "TRV-2024-551903",
        client_email: null,
        confidence: "high",
      },
      client_context: null,
      recommended_workflow_id: "carrier_mortgagee_update_v2",
    };
  },

  async getWorkflowResult(instanceId: string): Promise<WorkflowResultEnvelope> {
    await wait(80);
    return {
      instance_id: instanceId,
      status: "completed",
      completed_at: new Date().toISOString(),
      result: {
        summary: "Workflow completed successfully (stub).",
        pages_visited: 3,
      },
    };
  },

  async triggerPrefill(
    targetWorkflowId: string,
    clientSk?: number | null,
    sourceDocumentText?: string,
    alreadyExtracted?: Record<string, unknown>,
    attachmentText?: string,
    internetMessageId?: string | null,
    mailboxAddress?: string | null,
  ): Promise<{ instance_id: string }> {
    // Same arity-parity pattern as reanalysePrefill below.
    void clientSk;
    void sourceDocumentText;
    void alreadyExtracted;
    void attachmentText;
    void internetMessageId;
    void mailboxAddress;
    await wait(120);
    return { instance_id: `stub-prefill-${targetWorkflowId}-${Date.now()}` };
  },

  async reanalysePrefill(
    targetWorkflowId: string,
    clientSk?: number | null,
    sourceDocumentText?: string,
    alreadyExtracted?: Record<string, unknown>,
    attachmentText?: string,
    internetMessageId?: string | null,
    mailboxAddress?: string | null,
  ): Promise<{ instance_id: string }> {
    // Arity parity with the real implementation is asserted by
    // `orchestratorApi.test.ts`; see reanalyzeEmail above.
    void clientSk;
    void sourceDocumentText;
    void alreadyExtracted;
    void attachmentText;
    void internetMessageId;
    void mailboxAddress;
    await wait(120);
    // A DIFFERENT instance id every press, mirroring the fresh idempotency key
    // — a stub that returned the triggerPrefill id would make the stub pane
    // demonstrate the exact silent-success bug the real key avoids.
    return {
      instance_id: `stub-reanalyse-prefill-${targetWorkflowId}-${Date.now()}`,
    };
  },

  async pollPrefill(_instanceId: string): Promise<PrefillResult> {
    await wait(800);
    return {
      prefill_values: {
        policy_number: "TRV-2024-447821",
        carrier_id: "travelers",
      },
      field_confidence: {
        policy_number: "high",
        carrier_id: "medium",
      },
    };
  },

  async resumeInstance(
    instanceId: string,
    _userInput: Record<string, unknown>,
  ): Promise<OrchestratorResponse> {
    await wait(200);
    return { instance_id: instanceId, status: "running" } as OrchestratorResponse;
  },

  async checkEdocs(_instanceId: string): Promise<{ outcome: string }> {
    await wait(150);
    return { outcome: "no_edoc_yet" };
  },

  async runSentReplySweep(): Promise<SentReplySweepOutcome> {
    await wait(150);
    // Plural counts on purpose: the stub should exercise the "whole backlog"
    // wording, not the flattering one-of-each case.
    return { pending: 3, mailboxes: 2, runId: "stub-sweep-run" };
  },

  async getMyKpis(_window: "7d"): Promise<WorkflowKpis> {
    await wait(200);
    return { completed_count: 12, hours_saved: 4.2, window: "7d", since: new Date(Date.now() - 7 * 86400_000).toISOString() };
  },

  async getMyCredentialStatus(): Promise<UserCredentialStatus[]> {
    await wait(120);
    return [
      {
        credential_name: "epic-user-login",
        display_name: "Epic User Login",
        status: "needs_update",
        last_updated: new Date(Date.now() - 7 * 86_400_000).toISOString(),
      },
    ];
  },

  async getSpPool(): Promise<SpPoolSlot[]> {
    await wait(80);
    return [
      { slot: 1, email: "sp1@example.com", status: "available" },
      { slot: 2, email: "sp2@example.com", status: "in_use" },
    ];
  },

  async getSpPoolReadiness(): Promise<number> {
    await wait(80);
    // Matches the two slots the stub getSpPool returns.
    return 2;
  },

  async getMyOversightLevel(): Promise<OversightLevel | null> {
    await wait(80);
    return "HIGH";
  },

  async updateMyFlowPassword(_name: string, _value: string): Promise<void> {
    await wait(200);
  },

  async listWatchedMailboxes(): Promise<ListWatchedMailboxesResponse> {
    await wait(80);
    return {
      mailboxes: [
        {
          mailbox_key: "dev.user@highstreetpartners.com",
          entered_address: "dev.user@highstreetpartners.com",
          is_watched: true,
          last_verified_at: new Date().toISOString(),
          statement_version: "v1",
          workflow_id: "dev.user@highstreetpartners.com",
          watch_state: "watching",
          intake_mode: "suggest",
          owning_branch: "Dev Branch",
          can_set_intake_mode: true,
          intake_mode_options: ["off", "suggest"],
        },
      ],
      registry_health: {
        watched_count: 1,
        status: "ok",
        awaiting_branch_assignment: [],
        awaiting_branch_count: 0,
      },
      enrollable_mailboxes: [
        { address: "dev.user@highstreetpartners.com", kind: "personal" },
      ],
    };
  },

  async enrollMailbox(
    mailboxAddress: string,
    _statementVersion?: string,
  ): Promise<EnrollMailboxResponse> {
    await wait(200);
    return {
      mailbox_key: mailboxAddress,
      entered_address: mailboxAddress,
      is_watched: true,
      last_verified_at: new Date().toISOString(),
      statement_version: "v1",
      transitioned: true,
      status: "watched",
    };
  },

  async unenrollMailbox(
    mailboxAddress: string,
  ): Promise<UnenrollMailboxResponse> {
    await wait(150);
    return {
      mailbox_key: mailboxAddress,
      entered_address: mailboxAddress,
      is_watched: false,
      status: "unwatched",
    };
  },

  async setMailboxIntakeMode(
    mailboxAddress: string,
    mode: "off" | "suggest" | "auto",
  ): Promise<SetMailboxIntakeModeResponse> {
    await wait(150);
    return {
      mailbox_key: mailboxAddress,
      intake_mode: mode,
      previous_intake_mode: mode === "suggest" ? "off" : "suggest",
      authority: "branch_admin",
      status: "ok",
    };
  },

  async linkMailboxAccess(mailboxAddress: string, messageId: string): Promise<LinkMailboxAccessResponse> {
    await wait(50);
    // The stub links whenever it is asked; the real API proves access with this id.
    void messageId;
    return { status: "ok", mailbox_key: mailboxAddress.trim().toLowerCase(), linked: true, newly_linked: false };
  },
};

// ---------------------------------------------------------------------------
// Mode selection
// ---------------------------------------------------------------------------

const USE_STUB_API = resolveStubFlag();

export const orchestratorApi: OrchestratorApi = USE_STUB_API ? stubApi : realApi;

/**
 * Test helper — pick which implementation to use without rebuilding the
 * module. Restores via the returned undo function.
 */
export function _setOrchestratorApiForTests(
  impl: OrchestratorApi,
): () => void {
  const original = (orchestratorApi as unknown as { __backing?: OrchestratorApi })
    .__backing ?? orchestratorApi;
  // We can't reassign a const export; we mutate a Proxy-friendly façade
  // instead. Tests import `orchestratorApi`; if they need to swap, they
  // should import `realApi` / `stubApi` directly (exported below).
  void original;
  void impl;
  return () => undefined;
}

/** Direct named exports so tests can target each impl unambiguously. */
export const orchestratorRealApi = realApi;
export const orchestratorStubApi = stubApi;

/**
 * Convenience helper for the stub-parity assertion: returns the list of
 * methods every implementation must expose. Used by the unit test to
 * iterate without hard-coding the list twice.
 */
export const ORCHESTRATOR_API_METHODS: ReadonlyArray<keyof OrchestratorApi> = [
  "primeAuth",
  "getCatalog",
  "getCatalogEntry",
  "listInstances",
  "getStatus",
  "getInstanceLogs",
  "getPushChannel",
  "listWaiting",
  "listReadyBriefs",
  "ignoreReadyBrief",
  "acknowledgeRunWarning",
  "getReadyBrief",
  "storeBriefPrefill",
  "getEmailRunState",
  "getUserPushChannel",
  "getWaitingOnMe",
  "listWatchedMailboxes",
  "enrollMailbox",
  "unenrollMailbox",
  "setMailboxIntakeMode",
  "linkMailboxAccess",
  "joinSignalRGroup",
  "pingSignalR",
  "cancelInstance",
  "killExecPlaneSession",
  "getArtifacts",
  "getArtifactPayload",
  "getHitlHistory",
  "getRunDecisions",
  "recordTouchpoints",
  "getFeatureFlag",
  "triggerWorkflow",
  "getClientDetails",
  "getClientPolicies",
  "searchClientsLegacy",
  "searchClientsViaWorkflows",
  "getMyKpis",
  "execute",
  "approve",
  "analyzeEmail",
  "pollAnalysis",
  "triggerCarrierWorkflow",
  "analyzeEmailSync",
  "reanalyzeEmail",
  "getWorkflowResult",
  "triggerPrefill",
  "reanalysePrefill",
  "pollPrefill",
  "resumeInstance",
  "checkEdocs",
  "runSentReplySweep",
  "getMyCredentialStatus",
  "getMyOversightLevel",
  "updateMyFlowPassword",
  "getSpPool",
  "getSpPoolReadiness",
];

/**
 * Internal request helper exposed for tests (and only tests) — they
 * exercise retry-once + ApiError shape without touching real fetch.
 */
export const _internalRequest = request;
export const _DEMO = {
  ACTIVE_WORKFLOWS: DEMO_ACTIVE_WORKFLOWS,
  CATALOG: DEMO_CATALOG,
  HITL_QUESTION: DEMO_HITL_QUESTION,
};
